<div class="wizard-container">
  <p-toast position="top-right" role="status" aria-live="polite" aria-atomic="true"></p-toast>

  <!-- Loading Spinner Overlay -->
  @if (isLoading()) {
    <div class="loading-overlay" role="status" aria-live="polite">
      <div class="loading-content">
        <p-progressSpinner [ariaLabel]="loadingMessage()"></p-progressSpinner>
        <p class="mt-3">{{ loadingMessage() }}</p>
      </div>
    </div>
  }

  <!-- Content -->
  <div class="step-content">
    <!-- ================= STEP 0: SELEZIONE FORNITORE ================= -->
    @if (currentStep() === 0) {
      <!-- Breadcrumb -->
      <div class="mb-4">
        <p-breadcrumb
          styleClass="custom-breadcrumb"
          [model]="breadcrumbItems()"
          [home]="{ icon: 'pi pi-home', routerLink: '/', ariaLabel: 'Home' }"
        >
        </p-breadcrumb>
      </div>

      <!-- Page Header -->
      <section class="page-header mb-5" role="region" aria-labelledby="step0-heading">
        <div class="header-content">
          <div class="header-left">
            <i [class]="selectionListIcon() + ' header-icon'" aria-hidden="true"></i>
            <div class="header-text">
              <h1 id="step0-heading">Selezione {{ selectionEntityLabel() }}</h1>
              <p class="subtitle">
                Seleziona il {{ selectionEntityLabel().toLowerCase() }} per cui creare il contratto
              </p>
            </div>
          </div>
        </div>
      </section>

      <app-registry-table
        [title]="selectionListTitle()"
        [icon]="selectionListIcon()"
        [columns]="supplierColumns"
        [data]="suppliers()"
        [loading]="false"
        [showClearFiltersButton]="true"
        (onSelectClick)="selectSupplier($event)"
      >
      </app-registry-table>
    }

    <!-- ================= STEP 1: CONTRATTO ================= -->
    @if (currentStep() === 1) {
      <!-- Breadcrumb -->
      <div class="mb-4">
        <p-breadcrumb
          styleClass="custom-breadcrumb"
          [model]="breadcrumbItems()"
          [home]="{ icon: 'pi pi-home', routerLink: '/', ariaLabel: 'Home' }"
        >
        </p-breadcrumb>
      </div>

      <!-- Page Header -->
      <section class="page-header mb-5" role="region" aria-labelledby="step1-heading">
        <div class="header-content">
          <div class="header-left">
            <i class="pi pi-file-edit header-icon" aria-hidden="true"></i>
            <div class="header-text">
              <h1 id="step1-heading">Compilazione Contratto</h1>
              <p class="professional-info">
                @if (isSelectedEntityProfessional()) {
                  <strong>{{ selectedProfessionalBusinessName() }}</strong><br>
                  ID: {{ selectedProfessionalId() }}

                @if (selectedProfessionalCategory()) {
                  &nbsp;·&nbsp;{{ selectedProfessionalCategory() }}
                }
              } @else if (selectedSupplierId()) {
                @if (selectedSupplierDisplayName()) {
                  <strong>{{ selectedSupplierDisplayName() }}</strong><br>
                }
                ID: {{ selectedSupplierId() }}
              }
              </p>
              <p class="subtitle">Inserisci i dati principali del contratto</p>
            </div>
          </div>
          <!-- Completion Indicator -->
          <div class="completion-badge" [attr.aria-label]="isStep1Completed() ? 'Step completato' : 'Step non completato'">
            @if (isStep1Completed()) {
              <i class="pi pi-check-circle completed-icon" aria-hidden="true"></i>
              <span class="completion-text">Completato</span>
            }
            @else {
              <i class="pi pi-circle incomplete-icon" aria-hidden="true"></i>
              <span class="completion-text">Da completare</span>
            }
          </div>
        </div>
      </section>

      <form [formGroup]="step1Form" (ngSubmit)="saveStep1AndContinue()" class="p-fluid">
        <!-- SEZIONE 1: INFORMAZIONI GENERALI DEL CONTRATTO -->
        <section
          class="surface-0 border-round p-5 mb-4 shadow-1"
          role="region"
          aria-labelledby="section1-heading"
        >
          <div class="flex align-items-center gap-3 mb-4 pb-3 border-bottom-1 border-200">
            <i class="pi pi-file-edit text-2xl" style="color: #00125b" aria-hidden="true"></i>
            <h2 id="section1-heading" class="text-xl font-semibold m-0" style="color: #00125b">
              Informazioni Generali del Contratto
            </h2>
          </div>

          <div class="grid">
            <!-- ID Fiduciario e Categoria (solo per professional) -->
            @if (isSelectedEntityProfessional()) {
              <div class="field col-12 md:col-4">
                <label for="professionalId" class="font-semibold block mb-2">
                  ID Fiduciario
                </label>
                <input
                  pInputText
                  id="professionalId"
                  [value]="selectedProfessionalId()"
                  readonly
                  disabled
                  class="w-full"
                  aria-readonly="true"
                />
              </div>

              <div class="field col-12 md:col-4">
                <label for="professionalCategory" class="font-semibold block mb-2">
                  Categoria Fiduciario
                </label>
                <input
                  pInputText
                  id="professionalCategory"
                  [value]="selectedProfessionalCategory()"
                  readonly
                  disabled
                  class="w-full"
                  aria-readonly="true"
                />
              </div>
            }

            <div class="field col-12 md:col-4">
              <label for="tipoContratto" class="font-semibold block mb-2">
                Tipo contratto <span class="text-red-500" aria-hidden="true">*</span>
              </label>
              <p-select
                id="tipoContratto"
                formControlName="contractType"
                [options]="tipoContrattoOptions()"
                optionLabel="valueDescription"
                optionValue="valueKey"
                placeholder="Seleziona tipo"
                class="w-full"
                [attr.aria-required]="true"
                [attr.aria-invalid]="
                  step1Form.get('contractType')?.invalid && step1Form.get('contractType')?.touched
                "
                [attr.aria-describedby]="
                  step1Form.get('contractType')?.invalid && step1Form.get('contractType')?.touched
                    ? 'tipoContratto-error'
                    : null
                "
              ></p-select>
              @if (
                step1Form.get('contractType')?.invalid && step1Form.get('contractType')?.touched
              ) {
                <small id="tipoContratto-error" class="text-red-500" role="alert"
                  >Campo obbligatorio</small
                >
              }
            </div>

            <div class="field col-12 md:col-4" [hidden]="isBackofficeProfessional()">
              <label for="tipologiaServizio" class="font-semibold block mb-2">
                Tipologia servizio - commodity
                <span class="text-red-500" aria-hidden="true">*</span>
              </label>
              <p-select
                id="tipologiaServizio"
                formControlName="serviceType"
                [options]="serviceTypes()"
                optionLabel="valueDescription"
                optionValue="valueKey"
                placeholder="Seleziona tipologia"
                class="w-full"
                [attr.aria-required]="true"
                [attr.aria-invalid]="
                  step1Form.get('serviceType')?.invalid && step1Form.get('serviceType')?.touched
                "
                [attr.aria-describedby]="
                  step1Form.get('serviceType')?.invalid && step1Form.get('serviceType')?.touched
                    ? 'tipologiaServizio-error'
                    : null
                "
              ></p-select>
              @if (step1Form.get('serviceType')?.invalid && step1Form.get('serviceType')?.touched) {
                <small id="tipologiaServizio-error" class="text-red-500" role="alert"
                  >Campo obbligatorio</small
                >
              }
            </div>

            <div class="field col-12">
              <label class="font-semibold block mb-2">
                Entity Coinvolte <span class="text-red-500" aria-hidden="true">*</span>
              </label>

              <div formArrayName="involvedEntities" class="flex flex-column gap-3">
                @for (entity of involvedEntitiesArray.controls; track $index) {
                  <div [formGroup]="$any(entity)" class="surface-50 border-round p-4">
                    <div class="flex justify-content-between align-items-center mb-3">
                      <h4 class="text-md font-semibold m-0">
                        <i class="pi pi-building mr-2" aria-hidden="true"></i>
                        Entity Coinvolta #{{ $index + 1 }}
                      </h4>
                      <p-button
                        icon="pi pi-trash"
                        severity="danger"
                        size="small"
                        [text]="true"
                        (onClick)="removeInvolvedEntity($index)"
                        [disabled]="involvedEntitiesArray.length === 1"
                        [attr.aria-label]="'Elimina entity coinvolta #' + ($index + 1)"
                      ></p-button>
                    </div>

                    <div class="grid">
                      <div class="field col-12 md:col-4">
                        <label [for]="'entityCoinvolta-' + $index" class="font-semibold block mb-2">
                          Denominazione Entità <span class="text-red-500" aria-hidden="true">*</span>
                        </label>
                        <p-select
                          [id]="'entityCoinvolta-' + $index"
                          formControlName="entityCode"
                          [options]="getAvailableInvolvedEntityOptions($index)"
                          optionLabel="entityDescription"
                          optionValue="entityCode"
                          placeholder="Seleziona entità"
                          [showClear]="true"
                          (onChange)="onInvolvedEntityChange($index)"
                          class="w-full"
                        ></p-select>
                      </div>

                      <div class="field col-12 md:col-4">
                        <label [for]="'signatoryName-' + $index" class="font-semibold block mb-2">
                          Firmato da ({{ getEntityLabel($any(entity).get('entityCode')?.value) }})
                          <span class="text-red-500" aria-hidden="true">*</span>
                        </label>
                        <input
                          pInputText
                          [id]="'signatoryName-' + $index"
                          formControlName="signatoryName"
                          class="w-full"
                          placeholder="Inserisci firmatario"
                        />
                      </div>

                      <div class="field col-12 md:col-4">
                        <label [for]="'signatoryRole-' + $index" class="font-semibold block mb-2">
                          Ruolo Firmatario ({{ getEntityLabel($any(entity).get('entityCode')?.value) }})
                          <span class="text-red-500" aria-hidden="true">*</span>
                        </label>
                        <input
                          pInputText
                          [id]="'signatoryRole-' + $index"
                          formControlName="signatoryRole"
                          class="w-full"
                          placeholder="Inserisci ruolo firmatario"
                        />
                      </div>
                    </div>
                  </div>
                }
              </div>

              @if (
                step1Form.get('involvedEntities')?.invalid &&
                step1Form.get('involvedEntities')?.touched
              ) {
                <small id="entityCoinvolte-error" class="text-red-500 block mt-2" role="alert"
                  >È obbligatorio aggiungere almeno una entity coinvolta completa</small
                >
              }

              <div class="flex justify-content-end align-items-center gap-2 mt-3">
                @if (!canAddMoreInvolvedEntities()) {
                  <small class="text-muted">Tutte le entità disponibili sono state aggiunte</small>
                }
                <p-button
                  label="Aggiungi Entity Coinvolta"
                  icon="pi pi-plus"
                  size="small"
                  (onClick)="addInvolvedEntity()"
                  [disabled]="!canAddMoreInvolvedEntities()"
                ></p-button>
              </div>
            </div>

            <div class="field col-12" [hidden]="isBackofficeProfessional()">
              <label for="contractObject" class="font-semibold block mb-2">
                Oggetto del Contratto <span class="text-red-500" aria-hidden="true">*</span>
              </label>
              <textarea
                pTextarea
                id="contractObject"
                formControlName="contractSubject"
                rows="2"
                class="w-full"
                placeholder="es. fornitore per la pubblicazione dei relitti in ambito motor"
                [attr.aria-required]="true"
                [attr.aria-invalid]="
                  step1Form.get('contractSubject')?.invalid &&
                  step1Form.get('contractSubject')?.touched
                "
                [attr.aria-describedby]="
                  step1Form.get('contractSubject')?.invalid &&
                  step1Form.get('contractSubject')?.touched
                    ? 'contractObject-error'
                    : null
                "
              ></textarea>
              <app-validation-error 
                [control]="step1Form.get('contractSubject')" 
                errorId="contractObject-error"
                mode="strict"
              />
            </div>

            <div class="field col-12 md:col-6" [hidden]="isBackofficeProfessional()">
              <label for="attivitaEster" class="font-semibold block mb-2">
                Attività esternalizzata <span class="text-red-500" aria-hidden="true">*</span>
              </label>
              <p-select
                id="attivitaEster"
                formControlName="outsourcedActivity"
                [options]="attivitaEsterOptions()"
                optionLabel="valueDescription"
                optionValue="valueKey"
                placeholder="Seleziona"
                class="w-full"
                [attr.aria-required]="true"
                [attr.aria-invalid]="
                  step1Form.get('outsourcedActivity')?.invalid &&
                  step1Form.get('outsourcedActivity')?.touched
                "
                [attr.aria-describedby]="
                  step1Form.get('outsourcedActivity')?.invalid &&
                  step1Form.get('outsourcedActivity')?.touched
                    ? 'attivitaEster-error'
                    : null
                "
              ></p-select>
              @if (
                step1Form.get('outsourcedActivity')?.invalid &&
                step1Form.get('outsourcedActivity')?.touched
              ) {
                <small id="attivitaEster-error" class="text-red-500" role="alert"
                  >Campo obbligatorio</small
                >
              }
            </div>

            <div class="field col-12 md:col-6" [hidden]="isBackofficeProfessional()">
              <label for="ownerSLA" class="font-semibold block mb-2">
                Owner SLA & KPI@if (isSlaKpiPersonRequired()) {<span class="text-red-500" aria-hidden="true">*</span>}
              </label>
              <input
                pInputText
                id="ownerSLA"
                formControlName="slaKpiPerson"
                class="w-full"
                placeholder="Nome cognome o ufficio"
                [attr.aria-required]="isSlaKpiPersonRequired()"
                [attr.aria-invalid]="
                  step1Form.get('slaKpiPerson')?.invalid && step1Form.get('slaKpiPerson')?.touched
                "
                [attr.aria-describedby]="
                  step1Form.get('slaKpiPerson')?.invalid && step1Form.get('slaKpiPerson')?.touched
                    ? 'ownerSLA-error'
                    : null
                "
              />
              <app-validation-error
                [control]="step1Form.get('slaKpiPerson')"
                errorId="ownerSLA-error"
                mode="name"
              />
            </div>

            <div class="field col-12 md:col-4">
              <label for="startDate" class="font-semibold block mb-2">
                Data Inizio <span class="text-red-500" aria-hidden="true">*</span>
              </label>
              <p-datepicker
                id="startDate"
                formControlName="startDate"
                dateFormat="dd/mm/yy"
                [showIcon]="true"
                placeholder="Seleziona data"
                class="w-full"
                [attr.aria-required]="true"
                [attr.aria-invalid]="
                  step1Form.get('startDate')?.invalid && step1Form.get('startDate')?.touched
                "
                [attr.aria-describedby]="
                  step1Form.get('startDate')?.invalid && step1Form.get('startDate')?.touched
                    ? 'startDate-error'
                    : null
                "
              ></p-datepicker>
              @if (step1Form.get('startDate')?.invalid && step1Form.get('startDate')?.touched) {
                <small id="startDate-error" class="text-red-500" role="alert"
                  >Campo obbligatorio</small
                >
              }
            </div>

            <div class="field col-12 md:col-4">
              <label for="endDate" class="font-semibold block mb-2">
                Data Fine <span class="text-red-500" aria-hidden="true">*</span>
              </label>
              <p-datepicker
                id="endDate"
                formControlName="endDate"
                [minDate]="step1Form.get('startDate')?.value"
                dateFormat="dd/mm/yy"
                [showIcon]="true"
                placeholder="Seleziona data"
                class="w-full"
                [attr.aria-required]="true"
                [attr.aria-invalid]="
                  (step1Form.get('endDate')?.invalid || step1Form.hasError('endDateBeforeStart')) &&
                  step1Form.get('endDate')?.touched
                "
                [attr.aria-describedby]="
                  (step1Form.get('endDate')?.invalid || step1Form.hasError('endDateBeforeStart')) &&
                  step1Form.get('endDate')?.touched
                    ? 'endDate-error'
                    : null
                "
              ></p-datepicker>
              @if (step1Form.get('endDate')?.invalid && step1Form.get('endDate')?.touched) {
                <small id="endDate-error" class="text-red-500" role="alert"
                  >Campo obbligatorio</small
                >
              }
              @if (step1Form.hasError('endDateBeforeStart')) {
                <small id="endDate-error" class="text-red-500" role="alert"
                  >La data fine deve essere successiva alla data inizio</small
                >
              }
            </div>

            <div class="field col-12 md:col-4">
              <label for="rinnovoTacito" class="font-semibold block mb-2">
                Rinnovo Tacito <span class="text-red-500" aria-hidden="true">*</span>
              </label>
              <p-select
                id="rinnovoTacito"
                formControlName="automaticRenewal"
                [options]="rinnovoTacitoOptions()"
                optionLabel="valueDescription"
                optionValue="valueKey"
                placeholder="Seleziona"
                class="w-full"
                [attr.aria-required]="true"
                [attr.aria-invalid]="
                  step1Form.get('automaticRenewal')?.invalid &&
                  step1Form.get('automaticRenewal')?.touched
                "
                [attr.aria-describedby]="
                  step1Form.get('automaticRenewal')?.invalid &&
                  step1Form.get('automaticRenewal')?.touched
                    ? 'rinnovoTacito-error'
                    : null
                "
              ></p-select>
              @if (
                step1Form.get('automaticRenewal')?.invalid &&
                step1Form.get('automaticRenewal')?.touched
              ) {
                <small id="rinnovoTacito-error" class="text-red-500" role="alert"
                  >Campo obbligatorio</small
                >
              }
            </div>

            @if (isNumberOfRenewalsRequired()) {
              <div class="field col-12 md:col-4">
                <label for="numberOfRenewals" class="font-semibold block mb-2">
                  Numero rinnovi <span class="text-red-500" aria-hidden="true">*</span>
                </label>
                <p-inputnumber
                  inputId="numberOfRenewals"
                  formControlName="numberOfRenewals"
                  [min]="2"
                  [maxFractionDigits]="0"
                  [useGrouping]="false"
                  class="w-full"
                  [attr.aria-required]="true"
                  [attr.aria-invalid]="step1Form.get('numberOfRenewals')?.invalid && step1Form.get('numberOfRenewals')?.touched"
                  [attr.aria-describedby]="step1Form.get('numberOfRenewals')?.invalid && step1Form.get('numberOfRenewals')?.touched ? 'numberOfRenewals-error' : null"
                />
                @if (step1Form.get('numberOfRenewals')?.invalid && step1Form.get('numberOfRenewals')?.touched) {
                  <small id="numberOfRenewals-error" class="text-red-500" role="alert">
                    Inserisci un numero intero superiore a 1.
                  </small>
                }
              </div>
            }

            @if (
              step1Form.get('automaticRenewal')?.value === 'YES' ||
              step1Form.get('automaticRenewal')?.value === 'ANNUAL'
            ) {
              <div class="field col-12 md:col-6">
                <label for="preavvisoRinnovoGiorni" class="font-semibold block mb-2">
                  Preavviso per rinnovo contratto (giorni)
                  <span class="text-red-500" aria-hidden="true">*</span>
                </label>
                <p-inputnumber
                  id="preavvisoRinnovoGiorni"
                  formControlName="renewalNoticeDays"
                  [min]="0"
                  placeholder="90"
                  class="w-full"
                  [attr.aria-required]="true"
                  [attr.aria-invalid]="
                    step1Form.get('renewalNoticeDays')?.invalid &&
                    step1Form.get('renewalNoticeDays')?.touched
                  "
                  [attr.aria-describedby]="
                    (step1Form.get('renewalNoticeDays')?.invalid &&
                    step1Form.get('renewalNoticeDays')?.touched
                      ? 'preavvisoRinnovoGiorni-error '
                      : '') + 'preavvisoRinnovoGiorni-desc'
                  "
                ></p-inputnumber>
                <small id="preavvisoRinnovoGiorni-desc" class="text-500"
                  >Il sistema prevede automaticamente 90 giorni</small
                >
                @if (
                  step1Form.get('renewalNoticeDays')?.invalid &&
                  step1Form.get('renewalNoticeDays')?.touched
                ) {
                  <small id="preavvisoRinnovoGiorni-error" class="text-red-500" role="alert"
                    >Campo obbligatorio</small
                  >
                }
              </div>
            }

            <div class="field col-12 md:col-4">
              <label for="disdettaGiorni" class="font-semibold block mb-2">
                Disdetta (in giorni) <span class="text-red-500" aria-hidden="true">*</span>
              </label>
              <p-inputnumber
                id="disdettaGiorni"
                formControlName="terminationNoticeDays"
                [min]="0"
                placeholder="90"
                class="w-full"
                [attr.aria-required]="true"
                [attr.aria-invalid]="
                  step1Form.get('terminationNoticeDays')?.invalid &&
                  step1Form.get('terminationNoticeDays')?.touched
                "
                [attr.aria-describedby]="
                  step1Form.get('terminationNoticeDays')?.invalid &&
                  step1Form.get('terminationNoticeDays')?.touched
                    ? 'disdettaGiorni-error'
                    : null
                "
              ></p-inputnumber>
              @if (
                step1Form.get('terminationNoticeDays')?.invalid &&
                step1Form.get('terminationNoticeDays')?.touched
              ) {
                <small id="disdettaGiorni-error" class="text-red-500" role="alert"
                  >Campo obbligatorio</small
                >
              }
            </div>

            <div class="field col-12 md:col-4">
              <fieldset>
                <legend class="font-semibold block mb-3">Sinergie</legend>
                <div class="flex gap-4" role="radiogroup" aria-label="Sinergie">
                  @for (option of yesNoOptions; track option.value) {
                    <div class="flex align-items-center">
                      <p-radiobutton
                        name="synergies"
                        [inputId]="'synergies-' + option.value"
                        [value]="option.value"
                        formControlName="synergies"
                      ></p-radiobutton>
                      <label [for]="'synergies-' + option.value" class="ml-2">{{
                        option.label
                      }}</label>
                    </div>
                  }
                </div>
              </fieldset>
            </div>

            @if (step1Form.get('synergies')?.value) {
              <div class="field col-12 md:col-8">
                <label for="sinergieCompagnie" class="font-semibold block mb-2">
                  Compagnie con sinergie<span class="text-red-500" aria-hidden="true">*</span>
                </label>
                <p-multiselect
                  id="sinergieCompagnie"
                  formControlName="synergiesCompanies"
                  [options]="sinergieCompagnieOptions()"
                  optionLabel="valueDescription"
                  optionValue="valueKey"
                  placeholder="Seleziona compagnie"
                  class="w-full"
                  [attr.aria-required]="true"
                  [attr.aria-invalid]="
                    step1Form.get('synergiesCompanies')?.invalid &&
                    step1Form.get('synergiesCompanies')?.touched
                  "
                  [attr.aria-describedby]="
                    step1Form.get('synergiesCompanies')?.invalid &&
                    step1Form.get('synergiesCompanies')?.touched
                      ? 'sinergieCompagnie-error'
                      : null
                  "
                ></p-multiselect>
                @if (
                  step1Form.get('synergiesCompanies')?.invalid &&
                  step1Form.get('synergiesCompanies')?.touched
                ) {
                  <small id="sinergieCompagnie-error" class="text-red-500" role="alert"
                    >Campo obbligatorio</small
                  >
                }
              </div>
            }

            <div class="field col-12 md:col-6" [hidden]="isBackofficeProfessional()">
              <label for="scCoinvolto" class="font-semibold block mb-2"> SC Coinvolto </label>
              <input
                pInputText
                id="scCoinvolto"
                formControlName="involvedSc"
                class="w-full"
                placeholder="es. SC65, SC22"
              />
            </div>

            <div class="field col-12 md:col-6" [hidden]="isBackofficeProfessional()">
              <label for="tipoFornitore" class="font-semibold block mb-2">
                Tipologia di Fornitore <span class="text-red-500" aria-hidden="true">*</span>
              </label>
              <p-select
                id="tipoFornitore"
                formControlName="supplierType"
                [options]="tipoFornitoreOptions()"
                optionLabel="valueDescription"
                optionValue="valueKey"
                placeholder="Seleziona tipologia"
                class="w-full"
                [attr.aria-required]="true"
                [attr.aria-invalid]="
                  step1Form.get('supplierType')?.invalid && step1Form.get('supplierType')?.touched
                "
                [attr.aria-describedby]="
                  step1Form.get('supplierType')?.invalid && step1Form.get('supplierType')?.touched
                    ? 'tipoFornitore-error'
                    : null
                "
              ></p-select>
              @if (
                step1Form.get('supplierType')?.invalid && step1Form.get('supplierType')?.touched
              ) {
                <small id="tipoFornitore-error" class="text-red-500" role="alert"
                  >Campo obbligatorio</small
                >
              }
            </div>

            <div class="field col-12 md:col-6" [hidden]="!isSelectedEntityProfessional()">
              <label for="toolGestionali" class="font-semibold block mb-2">
                Tool Gestionali <span class="text-red-500" aria-hidden="true">*</span>
              </label>
              <p-multiselect
                id="toolGestionali"
                formControlName="managementTools"
                [options]="toolGestionaliOptions()"
                optionLabel="valueDescription"
                optionValue="valueKey"
                placeholder="Seleziona tool"
                class="w-full"
                [attr.aria-required]="true"
                [attr.aria-invalid]="
                  step1Form.get('managementTools')?.invalid &&
                  step1Form.get('managementTools')?.touched
                "
                [attr.aria-describedby]="
                  step1Form.get('managementTools')?.invalid &&
                  step1Form.get('managementTools')?.touched
                    ? 'toolGestionali-error'
                    : null
                "
              ></p-multiselect>
              @if (
                step1Form.get('managementTools')?.invalid &&
                step1Form.get('managementTools')?.touched
              ) {
                <small id="toolGestionali-error" class="text-red-500" role="alert"
                  >Campo obbligatorio</small
                >
              }
            </div>

            <div class="field col-12 md:col-6" [hidden]="!isSelectedEntityProfessional()">
              <label for="network" class="font-semibold block mb-2">
                Network <span class="text-red-500" aria-hidden="true">*</span>
              </label>
              <p-multiselect
                id="network"
                formControlName="network"
                [options]="networkOptions()"
                optionLabel="valueDescription"
                optionValue="valueKey"
                placeholder="Seleziona network"
                class="w-full"
                [attr.aria-required]="true"
                [attr.aria-invalid]="
                  step1Form.get('network')?.invalid && step1Form.get('network')?.touched
                "
                [attr.aria-describedby]="
                  step1Form.get('network')?.invalid && step1Form.get('network')?.touched
                    ? 'network-error'
                    : null
                "
              ></p-multiselect>
              @if (step1Form.get('network')?.invalid && step1Form.get('network')?.touched) {
                <small id="network-error" class="text-red-500" role="alert"
                  >Campo obbligatorio</small
                >
              }
            </div>
          </div>
        </section>

        <!-- SEZIONE 2: CONTRATTO QUADRO -->
        <section
          class="surface-0 border-round p-5 mb-4 shadow-1"
          [hidden]="isBackofficeProfessional()"
          role="region"
          aria-labelledby="section2-heading"
        >
          <div class="flex align-items-center gap-3 mb-4 pb-3 border-bottom-1 border-200">
            <i class="pi pi-briefcase text-2xl" style="color: #00125b" aria-hidden="true"></i>
            <h2 id="section2-heading" class="text-xl font-semibold m-0" style="color: #00125b">
              Contratto Quadro
            </h2>
          </div>

          <div class="grid">
            <div class="field col-12 md:col-4">
              <fieldset>
                <legend class="font-semibold block mb-3">Accordo Quadro</legend>
                <div class="flex gap-4" role="radiogroup" aria-label="Accordo Quadro">
                  @for (option of yesNoOptions; track option.value) {
                    <div class="flex align-items-center">
                      <p-radiobutton
                        name="frameworkAgreement"
                        [inputId]="'frameworkAgreement-' + option.value"
                        [value]="option.value"
                        formControlName="frameworkAgreement"
                      ></p-radiobutton>
                      <label [for]="'frameworkAgreement-' + option.value" class="ml-2">{{
                        option.label
                      }}</label>
                    </div>
                  }
                </div>
              </fieldset>
            </div>

            @if (step1Form.get('frameworkAgreement')?.value) {
              <div class="field col-12 md:col-4">
                <label for="inizioAccordoQuadro" class="font-semibold block mb-2">
                  Inizio Accordo Quadro <span class="text-red-500" aria-hidden="true">*</span>
                </label>
                <p-datepicker
                  id="inizioAccordoQuadro"
                  formControlName="frameworkStartDate"
                  dateFormat="dd/mm/yy"
                  [showIcon]="true"
                  placeholder="Seleziona data"
                  class="w-full"
                  [attr.aria-required]="true"
                  [attr.aria-invalid]="
                    step1Form.get('frameworkStartDate')?.invalid &&
                    step1Form.get('frameworkStartDate')?.touched
                  "
                  [attr.aria-describedby]="
                    step1Form.get('frameworkStartDate')?.invalid &&
                    step1Form.get('frameworkStartDate')?.touched
                      ? 'inizioAccordoQuadro-error'
                      : null
                  "
                ></p-datepicker>
                @if (
                  step1Form.get('frameworkStartDate')?.invalid &&
                  step1Form.get('frameworkStartDate')?.touched
                ) {
                  <small id="inizioAccordoQuadro-error" class="text-red-500" role="alert"
                    >Campo obbligatorio</small
                  >
                }
              </div>

              <div class="field col-12 md:col-4">
                <label for="fineAccordoQuadro" class="font-semibold block mb-2">
                  Fine Accordo Quadro <span class="text-red-500" aria-hidden="true">*</span>
                </label>
                <p-datepicker
                  id="fineAccordoQuadro"
                  formControlName="frameworkEndDate"
                  [minDate]="step1Form.get('frameworkStartDate')?.value"
                  dateFormat="dd/mm/yy"
                  [showIcon]="true"
                  placeholder="Seleziona data"
                  class="w-full"
                  [attr.aria-required]="true"
                  [attr.aria-invalid]="
                    step1Form.get('frameworkEndDate')?.invalid &&
                    step1Form.get('frameworkEndDate')?.touched
                  "
                  [attr.aria-describedby]="
                    step1Form.get('frameworkEndDate')?.invalid &&
                    step1Form.get('frameworkEndDate')?.touched
                      ? 'fineAccordoQuadro-error'
                      : null
                  "
                ></p-datepicker>
                @if (
                  step1Form.get('frameworkEndDate')?.invalid &&
                  step1Form.get('frameworkEndDate')?.touched
                ) {
                  <small id="fineAccordoQuadro-error" class="text-red-500" role="alert"
                    >Campo obbligatorio</small
                  >
                }
              </div>

              <div class="field col-12 md:col-6">
                <label for="rinnovoTacitoQuadro" class="font-semibold block mb-2">
                  Rinnovo Tacito <span class="text-red-500" aria-hidden="true">*</span>
                </label>
                <p-select
                  id="rinnovoTacitoQuadro"
                  formControlName="frameworkAutomaticRenewal"
                  [options]="rinnovoTacitoOptions()"
                  optionLabel="valueDescription"
                  optionValue="valueKey"
                  placeholder="Seleziona"
                  class="w-full"
                  [attr.aria-required]="true"
                  [attr.aria-invalid]="
                    step1Form.get('frameworkAutomaticRenewal')?.invalid &&
                    step1Form.get('frameworkAutomaticRenewal')?.touched
                  "
                  [attr.aria-describedby]="
                    step1Form.get('frameworkAutomaticRenewal')?.invalid &&
                    step1Form.get('frameworkAutomaticRenewal')?.touched
                      ? 'rinnovoTacitoQuadro-error'
                      : null
                  "
                ></p-select>
                @if (
                  step1Form.get('frameworkAutomaticRenewal')?.invalid &&
                  step1Form.get('frameworkAutomaticRenewal')?.touched
                ) {
                  <small id="rinnovoTacitoQuadro-error" class="text-red-500" role="alert"
                    >Campo obbligatorio</small
                  >
                }
              </div>

              <div class="field col-12 md:col-6">
                <label for="preavvisoRinnovoQuadro" class="font-semibold block mb-2">
                  Preavviso per il rinnovo <span class="text-red-500" aria-hidden="true">*</span>
                </label>
                <p-select
                  id="preavvisoRinnovoQuadro"
                  formControlName="frameworkRenewalNoticeDays"
                  [options]="preavvisoRinnovoOptions()"
                  optionLabel="valueDescription"
                  optionValue="valueKey"
                  placeholder="Seleziona preavviso"
                  class="w-full"
                  [attr.aria-required]="true"
                  [attr.aria-invalid]="
                    step1Form.get('frameworkRenewalNoticeDays')?.invalid &&
                    step1Form.get('frameworkRenewalNoticeDays')?.touched
                  "
                  [attr.aria-describedby]="
                    step1Form.get('frameworkRenewalNoticeDays')?.invalid &&
                    step1Form.get('frameworkRenewalNoticeDays')?.touched
                      ? 'preavvisoRinnovoQuadro-error'
                      : null
                  "
                ></p-select>
                @if (
                  step1Form.get('frameworkRenewalNoticeDays')?.invalid &&
                  step1Form.get('frameworkRenewalNoticeDays')?.touched
                ) {
                  <small id="preavvisoRinnovoQuadro-error" class="text-red-500" role="alert"
                    >Campo obbligatorio</small
                  >
                }
              </div>
            }
          </div>
        </section>

        <!-- SEZIONE 3: ECONOMICS -->
        <section
          class="surface-0 border-round p-5 mb-4 shadow-1"
          role="region"
          aria-labelledby="section3-heading"
        >
          <div class="flex align-items-center gap-3 mb-4 pb-3 border-bottom-1 border-200">
            <i class="pi pi-euro text-2xl" style="color: #00125b" aria-hidden="true"></i>
            <h2 id="section3-heading" class="text-xl font-semibold m-0" style="color: #00125b">
              Economics
            </h2>
          </div>

          <div class="grid">
            <div class="field col-12 md:col-4">
              <fieldset>
                <legend class="font-semibold block mb-3">Corrispettivo Previsto</legend>
                <div class="flex gap-4" role="radiogroup" aria-label="Corrispettivo Previsto">
                  @for (option of yesNoOptions; track option.value) {
                    <div class="flex align-items-center">
                      <p-radiobutton
                        name="expectedFee"
                        [inputId]="'expectedFee-' + option.value"
                        [value]="option.value"
                        formControlName="expectedFee"
                      ></p-radiobutton>
                      <label [for]="'expectedFee-' + option.value" class="ml-2">{{
                        option.label
                      }}</label>
                    </div>
                  }
                </div>
              </fieldset>
            </div>

            <div class="field col-12 md:col-4" [hidden]="isBackofficeProfessional()">
              <fieldset>
                <legend class="font-semibold block mb-3">Rebates</legend>
                <div class="flex gap-4" role="radiogroup" aria-label="Rebates">
                  @for (option of yesNoOptions; track option.value) {
                    <div class="flex align-items-center">
                      <p-radiobutton
                        [inputId]="'rebates-' + option.value"
                        [value]="option.value"
                        formControlName="rebates"
                      ></p-radiobutton>
                      <label [for]="'rebates-' + option.value" class="ml-2">{{
                        option.label
                      }}</label>
                    </div>
                  }
                </div>
              </fieldset>
            </div>

            @if (step1Form.get('rebates')?.value && !isBackofficeProfessional()) {
              <div class="field col-12 md:col-4">
                <label for="noteRebates" class="font-semibold block mb-2"> Note Rebates </label>
                <input
                  pInputText
                  id="noteRebates"
                  formControlName="rebatesNote"
                  class="w-full"
                  placeholder="Inserisci note"
                />
              </div>
            }

            <div class="field col-12 md:col-4" [hidden]="isBackofficeProfessional()">
              <fieldset>
                <legend class="font-semibold block mb-3">Fattura caricata su SOL</legend>
                <div class="flex gap-4" role="radiogroup" aria-label="Fattura caricata su SOL">
                  @for (option of yesNoOptions; track option.value) {
                    <div class="flex align-items-center">
                      <p-radiobutton
                        name="invoiceOnSol"
                        [inputId]="'invoiceOnSol-' + option.value"
                        [value]="option.value"
                        formControlName="invoiceOnSol"
                      ></p-radiobutton>
                      <label [for]="'invoiceOnSol-' + option.value" class="ml-2">{{
                        option.label
                      }}</label>
                    </div>
                  }
                </div>
              </fieldset>
            </div>

            <div class="field col-12 md:col-4" [hidden]="isBackofficeProfessional()">
              <fieldset>
                <legend class="font-semibold block mb-3">Se Negativo, conto tecnico</legend>
                <div class="flex gap-4" role="radiogroup" aria-label="Se Negativo, conto tecnico">
                  @for (option of yesNoOptions; track option.value) {
                    <div class="flex align-items-center">
                      <p-radiobutton
                        name="technicalAccount"
                        [inputId]="'technicalAccount-' + option.value"
                        [value]="option.value"
                        formControlName="technicalAccount"
                      ></p-radiobutton>
                      <label [for]="'technicalAccount-' + option.value" class="ml-2">{{
                        option.label
                      }}</label>
                    </div>
                  }
                </div>
              </fieldset>
            </div>

            <div class="field col-12 md:col-4" [hidden]="isBackofficeProfessional()">
              <label for="tipologiaRipartizione" class="font-semibold block mb-2">
                Tipologia/Matrice Ripartizione
              </label>
              <p-select
                id="tipologiaRipartizione"
                formControlName="allocationMatrix"
                [options]="tipologiaRipartizioneOptions()"
                optionLabel="valueDescription"
                optionValue="valueKey"
                placeholder="Seleziona"
                class="w-full"
                [attr.aria-required]="false"
                [attr.aria-invalid]="
                  step1Form.get('allocationMatrix')?.invalid &&
                  step1Form.get('allocationMatrix')?.touched
                "
                [attr.aria-describedby]="
                  step1Form.get('allocationMatrix')?.invalid &&
                  step1Form.get('allocationMatrix')?.touched
                    ? 'tipologiaRipartizione-error'
                    : null
                "
              ></p-select>
              @if (
                step1Form.get('allocationMatrix')?.invalid &&
                step1Form.get('allocationMatrix')?.touched
              ) {
                <small id="tipologiaRipartizione-error" class="text-red-500" role="alert"
                  >Campo obbligatorio</small
                >
              }
            </div>

            <div class="field col-12 md:col-3" [hidden]="isBackofficeProfessional()">
              <label for="contoSpese" class="font-semibold block mb-2">
                Conto spese
              </label>
              <input
                pInputText
                id="contoSpese"
                formControlName="expenseAccount"
                class="w-full"
                placeholder="Inserisci conto spese"
                [attr.aria-required]="false"
                [attr.aria-invalid]="
                  step1Form.get('expenseAccount')?.invalid &&
                  step1Form.get('expenseAccount')?.touched
                "
                [attr.aria-describedby]="
                  step1Form.get('expenseAccount')?.invalid &&
                  step1Form.get('expenseAccount')?.touched
                    ? 'contoSpese-error'
                    : null
                "
              />
              @if (
                step1Form.get('expenseAccount')?.invalid && step1Form.get('expenseAccount')?.touched
              ) {
                <small id="contoSpese-error" class="text-red-500" role="alert"
                  >Campo obbligatorio</small
                >
              }
            </div>

            <div class="field col-12 md:col-3" [hidden]="isBackofficeProfessional()">
              <label for="valuta" class="font-semibold block mb-2">
                Valuta
              </label>
              <p-select
                id="valuta"
                formControlName="currency"
                [options]="currencyOptions()"
                optionLabel="valueDescription"
                optionValue="valueKey"
                placeholder="Seleziona valuta"
                [showClear]="false"
                class="w-full"
                [attr.aria-required]="false"
                [attr.aria-invalid]="
                  step1Form.get('currency')?.invalid && step1Form.get('currency')?.touched
                "
                [attr.aria-describedby]="
                  step1Form.get('currency')?.invalid && step1Form.get('currency')?.touched
                    ? 'valuta-error'
                    : null
                "
              ></p-select>
              @if (step1Form.get('currency')?.invalid && step1Form.get('currency')?.touched) {
                <small id="valuta-error" class="text-red-500" role="alert"
                  >Campo obbligatorio</small
                >
              }
            </div>

            <div class="field col-12 md:col-3" [hidden]="isBackofficeProfessional()">
              <label for="valoreDurataContratto" class="font-semibold block mb-2">
                Valore Durata Contratto IVA esclusa
              </label>
              <p-inputnumber
                id="valoreDurataContratto"
                formControlName="totalContractValue"
                mode="currency"
                [currency]="step1Form.get('currency')?.value || 'EUR'"
                currencyDisplay="symbol"
                locale="it-IT"
                placeholder="0,00"
                class="w-full"
              ></p-inputnumber>
            </div>

            <div class="field col-12 md:col-3" [hidden]="isBackofficeProfessional()">
              <label for="valoreAnnuale" class="font-semibold block mb-2">
                Valore Annuale IVA esclusa
              </label>
              <p-inputnumber
                id="valoreAnnuale"
                formControlName="annualValue"
                mode="currency"
                [currency]="step1Form.get('currency')?.value || 'EUR'"
                currencyDisplay="symbol"
                locale="it-IT"
                placeholder="0,00"
                class="w-full"
              ></p-inputnumber>
            </div>

            <div class="field col-12 md:col-4" [hidden]="isBackofficeProfessional()">
              <label for="periodicitaFattura" class="font-semibold block mb-2">
                Periodicità Ricezione Fattura <span class="text-red-500" aria-hidden="true">*</span>
              </label>
              <p-select
                id="periodicitaFattura"
                formControlName="invoiceFrequency"
                [options]="periodicitaFatturaOptions()"
                optionLabel="valueDescription"
                optionValue="valueKey"
                placeholder="Seleziona periodicità"
                class="w-full"
                [attr.aria-required]="true"
                [attr.aria-invalid]="
                  step1Form.get('invoiceFrequency')?.invalid &&
                  step1Form.get('invoiceFrequency')?.touched
                "
                [attr.aria-describedby]="
                  step1Form.get('invoiceFrequency')?.invalid &&
                  step1Form.get('invoiceFrequency')?.touched
                    ? 'periodicitaFattura-error'
                    : null
                "
              ></p-select>
              @if (
                step1Form.get('invoiceFrequency')?.invalid &&
                step1Form.get('invoiceFrequency')?.touched
              ) {
                <small id="periodicitaFattura-error" class="text-red-500" role="alert"
                  >Campo obbligatorio</small
                >
              }
            </div>
          </div>
        </section>

        <!-- SEZIONE 4: AMMINISTRAZIONE CONTRATTO -->
        <section
          class="surface-0 border-round p-5 mb-4 shadow-1"
          role="region"
          aria-labelledby="section4-heading"
        >
          <div class="flex align-items-center gap-3 mb-4 pb-3 border-bottom-1 border-200">
            <i class="pi pi-users text-2xl" style="color: #00125b" aria-hidden="true"></i>
            <h2 id="section4-heading" class="text-xl font-semibold m-0" style="color: #00125b">
              Amministrazione Contratto
            </h2>
          </div>

          <div class="grid">
            <div class="field col-12 md:col-6" [hidden]="isBackofficeProfessional()">
              <label for="gestioneInCarico" class="font-semibold block mb-2">
                Gestione Contratto in carico a
                <span class="text-red-500" aria-hidden="true">*</span>
              </label>
              <p-select
                id="gestioneInCarico"
                formControlName="managingArea"
                [options]="gestioneInCaricoOptions()"
                optionLabel="valueDescription"
                optionValue="valueKey"
                placeholder="Seleziona"
                class="w-full"
                [attr.aria-required]="true"
                [attr.aria-invalid]="
                  step1Form.get('managingArea')?.invalid && step1Form.get('managingArea')?.touched
                "
                [attr.aria-describedby]="
                  step1Form.get('managingArea')?.invalid && step1Form.get('managingArea')?.touched
                    ? 'gestioneInCarico-error'
                    : null
                "
              ></p-select>
              @if (
                step1Form.get('managingArea')?.invalid && step1Form.get('managingArea')?.touched
              ) {
                <small id="gestioneInCarico-error" class="text-red-500" role="alert"
                  >Campo obbligatorio</small
                >
              }
            </div>

            <div class="field col-12 md:col-6" [hidden]="isBackofficeProfessional()">
              <label for="riferimentoAreaClaims" class="font-semibold block mb-2">
                Riferimento Area Claims<span class="text-red-500" aria-hidden="true">*</span>
              </label>
              <p-select
                id="riferimentoAreaClaims"
                formControlName="claimsAreaReference"
                [options]="areaClaimsOptions()"
                optionLabel="valueDescription"
                optionValue="valueKey"
                placeholder="Seleziona area"
                class="w-full"
                [attr.aria-required]="true"
                [attr.aria-invalid]="
                  step1Form.get('claimsAreaReference')?.invalid &&
                  step1Form.get('claimsAreaReference')?.touched
                "
                [attr.aria-describedby]="
                  step1Form.get('claimsAreaReference')?.invalid &&
                  step1Form.get('claimsAreaReference')?.touched
                    ? 'riferimentoAreaClaims-error'
                    : null
                "
              ></p-select>
              @if (
                step1Form.get('claimsAreaReference')?.invalid &&
                step1Form.get('claimsAreaReference')?.touched
              ) {
                <small id="riferimentoAreaClaims-error" class="text-red-500" role="alert"
                  >Campo obbligatorio</small
                >
              }
            </div>

            <div class="field col-12 md:col-6" [hidden]="isBackofficeProfessional()">
              <label for="eventualeReferente" class="font-semibold block mb-2">
                Eventuale Referente<span class="text-red-500" aria-hidden="true">*</span>
              </label>
              <input
                pInputText
                id="eventualeReferente"
                formControlName="eventualReferent"
                class="w-full"
                placeholder="Nome e cognome"
                [attr.aria-required]="true"
                [attr.aria-invalid]="
                  step1Form.get('eventualReferent')?.invalid &&
                  step1Form.get('eventualReferent')?.touched
                "
                [attr.aria-describedby]="
                  step1Form.get('eventualReferent')?.invalid &&
                  step1Form.get('eventualReferent')?.touched
                    ? 'eventualeReferente-error'
                    : null
                "
              />
              <app-validation-error
                [control]="step1Form.get('eventualReferent')"
                errorId="eventualeReferente-error"
                mode="name"
              />
            </div>

            <div class="field col-12 md:col-6">
              <label for="buyerName" class="font-semibold block mb-2">
                Buyer<span class="text-red-500" aria-hidden="true">*</span>
              </label>
              <input
                pInputText
                id="buyerName"
                formControlName="buyer"
                class="w-full"
                placeholder="Nome buyer"
                [attr.aria-required]="true"
                [attr.aria-invalid]="
                  step1Form.get('buyer')?.invalid && step1Form.get('buyer')?.touched
                "
                [attr.aria-describedby]="
                  step1Form.get('buyer')?.invalid && step1Form.get('buyer')?.touched
                    ? 'buyerName-error'
                    : null
                "
              />
              <app-validation-error
                [control]="step1Form.get('buyer')"
                errorId="buyerName-error"
                mode="name"
              />
            </div>

            <div class="field col-12">
              <label for="struttureCoinvolte" class="font-semibold block mb-2">
                Strutture Coinvolte <span class="text-red-500" aria-hidden="true">*</span>
              </label>
              <p-multiselect
                id="struttureCoinvolte"
                formControlName="involvedStructures"
                [options]="struttureCoinvolteOptions()"
                optionLabel="valueDescription"
                optionValue="valueKey"
                placeholder="Seleziona strutture"
                class="w-full"
                [attr.aria-required]="true"
                [attr.aria-invalid]="
                  step1Form.get('involvedStructures')?.invalid &&
                  step1Form.get('involvedStructures')?.touched
                "
                [attr.aria-describedby]="
                  step1Form.get('involvedStructures')?.invalid &&
                  step1Form.get('involvedStructures')?.touched
                    ? 'struttureCoinvolte-error'
                    : null
                "
              ></p-multiselect>
              @if (
                step1Form.get('involvedStructures')?.invalid &&
                step1Form.get('involvedStructures')?.touched
              ) {
                <small id="struttureCoinvolte-error" class="text-red-500" role="alert"
                  >Campo obbligatorio</small
                >
              }
            </div>

            <div class="field col-12 md:col-6" [hidden]="isBackofficeProfessional()">
              <label for="destinatarioFattura" class="font-semibold block mb-2">
                Destinatario Fattura
              </label>
              <input
                pInputText
                id="destinatarioFattura"
                formControlName="invoiceRecipientEmail"
                class="w-full"
                placeholder="Nome del destinatario"
                [attr.aria-invalid]="
                  step1Form.get('invoiceRecipientEmail')?.invalid &&
                  step1Form.get('invoiceRecipientEmail')?.touched
                "
                [attr.aria-describedby]="
                  step1Form.get('invoiceRecipientEmail')?.invalid &&
                  step1Form.get('invoiceRecipientEmail')?.touched
                    ? 'destinatarioFattura-error'
                    : null
                "
              />
              <app-validation-error
                [control]="step1Form.get('invoiceRecipientEmail')"
                errorId="destinatarioFattura-error"
                mode="free-text"
              />
            </div>

            <div class="field col-12 md:col-6" [hidden]="isBackofficeProfessional()">
              <label for="ownerControlli" class="font-semibold block mb-2">
                Owner Controlli
              </label>
              <input
                pInputText
                id="ownerControlli"
                formControlName="controlsOwner"
                class="w-full"
                placeholder="Nome e cognome"
                [attr.aria-invalid]="
                  step1Form.get('controlsOwner')?.invalid && step1Form.get('controlsOwner')?.touched
                "
                [attr.aria-describedby]="
                  step1Form.get('controlsOwner')?.invalid && step1Form.get('controlsOwner')?.touched
                    ? 'ownerControlli-error'
                    : null
                "
              />
              <app-validation-error
                [control]="step1Form.get('controlsOwner')"
                errorId="ownerControlli-error"
                mode="name"
              />
            </div>

            <div class="field col-12 md:col-6" [hidden]="isBackofficeProfessional()">
              <label for="autorizzatorePagamento" class="font-semibold block mb-2">
                Autorizzatore al Pagamento
              </label>
              <input
                pInputText
                id="autorizzatorePagamento"
                formControlName="paymentAuthorizer"
                class="w-full"
                placeholder="Nome e cognome"
                [attr.aria-invalid]="
                  step1Form.get('paymentAuthorizer')?.invalid &&
                  step1Form.get('paymentAuthorizer')?.touched
                "
                [attr.aria-describedby]="
                  step1Form.get('paymentAuthorizer')?.invalid &&
                  step1Form.get('paymentAuthorizer')?.touched
                    ? 'autorizzatorePagamento-error'
                    : null
                "
              />
              <app-validation-error
                [control]="step1Form.get('paymentAuthorizer')"
                errorId="autorizzatorePagamento-error"
                mode="name"
              />
            </div>

            <div class="field col-12 md:col-6" [hidden]="isBackofficeProfessional()">
              <fieldset>
                <legend class="font-semibold block mb-3">Documentazione Presso IP</legend>
                <div class="flex gap-4" role="radiogroup" aria-label="Documentazione Presso IP">
                  @for (option of yesNoOptions; track option.value) {
                    <div class="flex align-items-center">
                      <p-radiobutton
                        name="documentationAtIP"
                        [inputId]="'documentationAtIP-' + option.value"
                        [value]="option.value"
                        formControlName="documentationAtIP"
                      ></p-radiobutton>
                      <label [for]="'documentationAtIP-' + option.value" class="ml-2">{{
                        option.label
                      }}</label>
                    </div>
                  }
                </div>
              </fieldset>
            </div>

            <div class="field col-12 md:col-4" [hidden]="isBackofficeProfessional()">
              <label for="firmaFornitore" class="font-semibold block mb-2">
                Contratto firmato da (firma Fornitore)
                <span class="text-red-500" aria-hidden="true">*</span>
              </label>
              <input
                pInputText
                id="firmaFornitore"
                formControlName="supplierSignatory"
                class="w-full"
                placeholder="Nome e cognome"
                [attr.aria-required]="true"
                [attr.aria-invalid]="
                  step1Form.get('supplierSignatory')?.invalid &&
                  step1Form.get('supplierSignatory')?.touched
                "
                [attr.aria-describedby]="
                  step1Form.get('supplierSignatory')?.invalid &&
                  step1Form.get('supplierSignatory')?.touched
                    ? 'firmaFornitore-error'
                    : null
                "
              />
              <app-validation-error
                [control]="step1Form.get('supplierSignatory')"
                errorId="firmaFornitore-error"
                mode="name"
              />
            </div>

            <div class="field col-12 md:col-4" [hidden]="isBackofficeProfessional()">
              <label for="ruoloFirmaFornitore" class="font-semibold block mb-2">
                Ruolo firma fornitore <span class="text-red-500" aria-hidden="true">*</span>
              </label>
              <input
                pInputText
                id="ruoloFirmaFornitore"
                formControlName="supplierSignatoryRole"
                class="w-full"
                placeholder="es. CEO, Socio unico"
                [attr.aria-required]="true"
                [attr.aria-invalid]="
                  step1Form.get('supplierSignatoryRole')?.invalid &&
                  step1Form.get('supplierSignatoryRole')?.touched
                "
                [attr.aria-describedby]="
                  step1Form.get('supplierSignatoryRole')?.invalid &&
                  step1Form.get('supplierSignatoryRole')?.touched
                    ? 'ruoloFirmaFornitore-error'
                    : null
                "
              />
              <app-validation-error
                [control]="step1Form.get('supplierSignatoryRole')"
                errorId="ruoloFirmaFornitore-error"
                mode="strict"
              />
            </div>

            @if (isSelectedEntityProfessional()) {
              <div class="field col-12 md:col-4">
                <label for="firmaFiduciario" class="font-semibold block mb-2">
                  Contratto Firmato da (Fiduciario)
                  <span class="text-red-500" aria-hidden="true">*</span>
                </label>
                <input
                  pInputText
                  id="firmaFiduciario"
                  formControlName="professionalSignatory"
                  class="w-full"
                  placeholder="Cognome e nome"
                  [attr.aria-required]="true"
                  [attr.aria-invalid]="
                    step1Form.get('professionalSignatory')?.invalid &&
                    step1Form.get('professionalSignatory')?.touched
                  "
                  [attr.aria-describedby]="
                    step1Form.get('professionalSignatory')?.invalid &&
                    step1Form.get('professionalSignatory')?.touched
                      ? 'firmaFiduciario-error'
                      : null
                  "
                />
                <app-validation-error
                  [control]="step1Form.get('professionalSignatory')"
                  errorId="firmaFiduciario-error"
                  mode="name"
                />
              </div>

              <div class="field col-12 md:col-4">
                <label for="ruoloFirmaFiduciario" class="font-semibold block mb-2">
                  Ruolo Firma Fiduciario <span class="text-red-500" aria-hidden="true">*</span>
                </label>
                <input
                  pInputText
                  id="ruoloFirmaFiduciario"
                  formControlName="professionalSignatoryRole"
                  class="w-full"
                  placeholder="es. Legale rappresentante"
                  [attr.aria-required]="true"
                  [attr.aria-invalid]="
                    step1Form.get('professionalSignatoryRole')?.invalid &&
                    step1Form.get('professionalSignatoryRole')?.touched
                  "
                  [attr.aria-describedby]="
                    step1Form.get('professionalSignatoryRole')?.invalid &&
                    step1Form.get('professionalSignatoryRole')?.touched
                      ? 'ruoloFirmaFiduciario-error'
                      : null
                  "
                />
                <app-validation-error
                  [control]="step1Form.get('professionalSignatoryRole')"
                  errorId="ruoloFirmaFiduciario-error"
                  mode="strict"
                />
              </div>
            }
          </div>
        </section>

        <!-- SEZIONE 5: VRF - TRATTAMENTO DATI PERSONALI -->
        <section
          class="surface-0 border-round p-5 mb-4 shadow-1"
          [hidden]="isBackofficeProfessional()"
          role="region"
          aria-labelledby="section5-heading"
        >
          <div class="flex align-items-center gap-3 mb-4 pb-3 border-bottom-1 border-200">
            <i class="pi pi-shield text-2xl" style="color: #00125b" aria-hidden="true"></i>
            <h2 id="section5-heading" class="text-xl font-semibold m-0" style="color: #00125b">
              VRF - Trattamento Dati Personali
            </h2>
          </div>

          <div class="grid">
            <div class="field col-12 md:col-6">
              <label for="casoUsoVRF" class="font-semibold block mb-2">
                Caso d'uso VRF <span class="text-red-500" aria-hidden="true">*</span>
              </label>
              <p-select
                id="casoUsoVRF"
                formControlName="vrfUseCase"
                [options]="casoUsoVRFOptions()"
                optionLabel="valueDescription"
                optionValue="valueKey"
                placeholder="Seleziona caso d'uso VRF"
                [showClear]="true"
                class="w-full"
                [attr.aria-required]="true"
                [attr.aria-invalid]="
                  step1Form.get('vrfUseCase')?.invalid && step1Form.get('vrfUseCase')?.touched
                "
                [attr.aria-describedby]="
                  step1Form.get('vrfUseCase')?.invalid && step1Form.get('vrfUseCase')?.touched
                    ? 'casoUsoVRF-error'
                    : null
                "
              ></p-select>
              @if (step1Form.get('vrfUseCase')?.invalid && step1Form.get('vrfUseCase')?.touched) {
                <small id="casoUsoVRF-error" class="text-red-500" role="alert"
                  >Campo obbligatorio</small
                >
              }
            </div>

            <!-- Campo GDPR nascosto per professional (sempre true), visibile per supplier -->
            @if (!isSelectedEntityProfessional()) {
              <div class="field col-12 md:col-6">
                <fieldset>
                  <legend class="font-semibold block mb-3">Gestisce dati ai fini GDPR?</legend>
                  <div class="flex gap-4" role="radiogroup" aria-label="Gestisce dati ai fini GDPR?">
                    @for (option of yesNoOptions; track option.value) {
                      <div class="flex align-items-center">
                        <p-radiobutton
                          name="gdprDataProcessing"
                          [inputId]="'gdprDataProcessing-' + option.value"
                          [value]="option.value"
                          formControlName="gdprDataProcessing"
                        ></p-radiobutton>
                        <label [for]="'gdprDataProcessing-' + option.value" class="ml-2">{{
                          option.label
                        }}</label>
                      </div>
                    }
                  </div>
                </fieldset>
              </div>
            }

            @if (step1Form.get('gdprDataProcessing')?.value) {
              <div class="field col-12 md:col-6">
                <label for="funzionePrivacy" class="font-semibold block mb-2">
                  Funzione competente materia Privacy<span class="text-red-500" aria-hidden="true"
                    >*</span
                  >
                </label>
                <input
                  pInputText
                  id="funzionePrivacy"
                  formControlName="privacyResponsibleFunction"
                  class="w-full"
                  placeholder="es. IP/Procurement"
                  [attr.aria-required]="true"
                  [attr.aria-invalid]="
                    step1Form.get('privacyResponsibleFunction')?.invalid &&
                    step1Form.get('privacyResponsibleFunction')?.touched
                  "
                  [attr.aria-describedby]="
                    step1Form.get('privacyResponsibleFunction')?.invalid &&
                    step1Form.get('privacyResponsibleFunction')?.touched
                      ? 'funzionePrivacy-error'
                      : null
                  "
                />
                @if (
                  step1Form.get('privacyResponsibleFunction')?.invalid &&
                  step1Form.get('privacyResponsibleFunction')?.touched
                ) {
                  <small id="funzionePrivacy-error" class="text-red-500" role="alert"
                    >La funzione competente è obbligatoria</small
                  >
                }
              </div>
            }

            <div class="field col-12 md:col-6">
              <fieldset>
                <legend class="font-semibold block mb-3">Invio dati extra SEE</legend>
                <div class="flex gap-4" role="radiogroup" aria-label="Invio dati extra SEE">
                  @for (option of yesNoOptions; track option.value) {
                    <div class="flex align-items-center">
                      <p-radiobutton
                        name="dataTransferOutsideSee"
                        [inputId]="'dataTransferOutsideSee-' + option.value"
                        [value]="option.value"
                        formControlName="dataTransferOutsideSee"
                      ></p-radiobutton>
                      <label [for]="'dataTransferOutsideSee-' + option.value" class="ml-2">{{
                        option.label
                      }}</label>
                    </div>
                  }
                </div>
              </fieldset>
            </div>
          </div>
        </section>

        <!-- SEZIONE 6: CONTRACT-IN -->
        <section
          class="surface-0 border-round p-5 mb-4 shadow-1"
          [hidden]="isBackofficeProfessional()"
          role="region"
          aria-labelledby="section6-heading"
        >
          <div class="flex align-items-center gap-3 mb-4 pb-3 border-bottom-1 border-200">
            <i class="pi pi-file-import text-2xl" style="color: #00125b" aria-hidden="true"></i>
            <h2 id="section6-heading" class="text-xl font-semibold m-0" style="color: #00125b">
              Contract-in
            </h2>
          </div>

          <div class="grid">
            <div class="field col-12 md:col-6">
              <label for="contractInNumber" class="font-semibold block mb-2">
                n. reference di Contract-in
              </label>
              <input
                pInputText
                id="contractInNumber"
                formControlName="contractInNumber"
                class="w-full"
                placeholder="es. 00245544"
              />
            </div>
          </div>
        </section>

        <!-- Action Buttons -->
        <div class="flex flex-wrap justify-content-end gap-3 mt-4">
          <p-button
            label="Salva Bozza"
            icon="pi pi-save"
            [outlined]="true"
            styleClass="btn-draft"
            (onClick)="saveDraft(1)"
            aria-label="Salva come bozza senza validazione"
            title="Salva come bozza senza validazione"
          ></p-button>
          <p-button
            label="Salva e Prosegui"
            icon="pi pi-arrow-right"
            iconPos="right"
            type="submit"
            title="Salva e prosegui al passo successivo"
          ></p-button>
        </div>
      </form>
    }

    <!-- ================= STEP 2: TARIFFE (Solo Professional) ================= -->
    @if (currentStep() === 2 && isBackofficeProfessional()) {
      <!-- Breadcrumb -->
      <div class="mb-4">
        <p-breadcrumb
          styleClass="custom-breadcrumb"
          [model]="breadcrumbItems()"
          [home]="{ icon: 'pi pi-home', routerLink: '/', ariaLabel: 'Home' }"
        >
        </p-breadcrumb>
      </div>

      <!-- Page Header -->
      <section class="page-header mb-5" role="region" aria-labelledby="step2-heading">
        <div class="header-content">
          <div class="header-left">
            <i class="pi pi-euro header-icon" aria-hidden="true"></i>
            <div class="header-text">
              <h1 id="step2-heading">Configurazione Tariffe</h1>
              <p class="professional-info">
                @if (isSelectedEntityProfessional()) {
                  <strong>{{ selectedProfessionalBusinessName() }}</strong><br>
                  ID: {{ selectedProfessionalId() }}

                @if (selectedProfessionalCategory()) {
                  &nbsp;·&nbsp;{{ selectedProfessionalCategory() }}
                }
              }
              </p>
              <p class="subtitle">Definisci le tariffe contrattuali per il fiduciario</p>
            </div>
          </div>
          <!-- Completion Indicator -->
          <div class="completion-badge" [attr.aria-label]="isStep2Completed() ? 'Step completato' : 'Step non completato'">
            @if (isStep2Completed()) {
              <i class="pi pi-check-circle completed-icon" aria-hidden="true"></i>
              <span class="completion-text">Completato</span>
            }
            @else {
              <i class="pi pi-circle incomplete-icon" aria-hidden="true"></i>
              <span class="completion-text">Da completare</span>
            }
          </div>
        </div>
      </section>

      <!-- Payment Rate Component -->
      <app-payment-rate
        [professionalType]="selectedSupplier()?.type || ''"
        [contractId]="contractId() || ''"
        (statusChanged)="onTariffeStatusChanged($event)"
        (saveAndProceed)="onTariffeSaveAndProceed()"
      ></app-payment-rate>
    }

    <!-- ================= STEP ATTO DI NOMINA (2 per Supplier, 3 per Professional) ================= -->
    @if ((currentStep() === 2 && !isBackofficeProfessional()) || (currentStep() === 3 && isBackofficeProfessional())) {
      <!-- Breadcrumb -->
      <div class="mb-4">
        <p-breadcrumb
          styleClass="custom-breadcrumb"
          [model]="breadcrumbItems()"
          [home]="{ icon: 'pi pi-home', routerLink: '/', ariaLabel: 'Home' }"
        >
        </p-breadcrumb>
      </div>

      <section class="page-header mb-5" role="region" aria-labelledby="step2-heading">
        <div class="header-content">
          <div class="header-left">
            <i class="pi pi-id-card header-icon" aria-hidden="true"></i>
            <div class="header-text">
              <h1 id="step2-heading">Atto di Nomina</h1>
              <p class="professional-info">
                @if (isSelectedEntityProfessional()) {
                  <strong>{{ selectedProfessionalBusinessName() }}</strong><br>
                  ID: {{ selectedProfessionalId() }}

                @if (selectedProfessionalCategory()) {
                  &nbsp;·&nbsp;{{ selectedProfessionalCategory() }}
                }
              } @else if (selectedSupplierId()) {
                @if (selectedSupplierDisplayName()) {
                  <strong>{{ selectedSupplierDisplayName() }}</strong><br>
                }
                ID: {{ selectedSupplierId() }}
              }
              </p>
              <p class="subtitle">Dati GDPR e responsabilità del trattamento</p>
            </div>
          </div>
          <!-- Completion Indicator -->
          <div class="completion-badge" [attr.aria-label]="(isSelectedEntityProfessional() ? isStep3Completed() : isStep2Completed()) ? 'Step completato' : 'Step non completato'">
            @if (isSelectedEntityProfessional() ? isStep3Completed() : isStep2Completed()) {
              <i class="pi pi-check-circle completed-icon" aria-hidden="true"></i>
              <span class="completion-text">Completato</span>
            }
            @else {
              <i class="pi pi-circle incomplete-icon" aria-hidden="true"></i>
              <span class="completion-text">Da completare</span>
            }
          </div>
        </div>
      </section>

      <form [formGroup]="step2Form" (ngSubmit)="saveStep2AndContinue()" class="p-fluid">
        <!-- SEZIONE: TITOLARE DEL TRATTAMENTO -->
        <section
          class="surface-0 border-round p-5 mb-4 shadow-1"
          role="region"
          aria-labelledby="titolari-heading"
        >
          <div class="flex align-items-center gap-3 mb-4 pb-3 border-bottom-1 border-200">
            <i class="pi pi-building text-2xl" style="color: #00125b" aria-hidden="true"></i>
            <h2 id="titolari-heading" class="text-xl font-semibold m-0" style="color: #00125b">
              Titolare del Trattamento
            </h2>
          </div>

          <div class="grid">
            <div class="col-12">
              <div class="mb-3">
                <h3 class="text-lg font-semibold m-0">Elenco Titolari del Trattamento</h3>
                <small class="text-muted"
                  >L'elenco è allineato alle entità coinvolte nel Contratto.</small
                >
              </div>
            </div>

            <!-- Iterazione su FormArray controllers -->
            @if (controllersArray.length === 0) {
              <div class="col-12">
                <p
                  class="text-center p-4 border-round"
                  [ngClass]="{
                    'text-muted surface-100': !(
                      step2Form.get('controllers')?.invalid && step2Form.get('controllers')?.touched
                    ),
                    'text-red-500 surface-red-50 border-1 border-red-500':
                      step2Form.get('controllers')?.invalid &&
                      step2Form.get('controllers')?.touched,
                  }"
                >
                  <i
                    class="pi mr-2"
                    aria-hidden="true"
                    [ngClass]="{
                      'pi-info-circle': !(
                        step2Form.get('controllers')?.invalid &&
                        step2Form.get('controllers')?.touched
                      ),
                      'pi-exclamation-triangle':
                        step2Form.get('controllers')?.invalid &&
                        step2Form.get('controllers')?.touched,
                    }"
                  ></i>
                  Nessuna entità coinvolta disponibile nel Contratto.
                </p>
                @if (
                  step2Form.get('controllers')?.invalid && step2Form.get('controllers')?.touched
                ) {
                  <small class="text-red-500 block text-center mb-3" role="alert">
                    <i class="pi pi-exclamation-circle mr-1" aria-hidden="true"></i>
                    È obbligatorio aggiungere almeno un Titolare del Trattamento
                  </small>
                }
              </div>
            }

            @for (controller of controllersArray.controls; track $index) {
              <div [formGroup]="$any(controller)" class="col-12">
                <div class="surface-50 border-round p-4 mb-3">
                  <div class="flex justify-content-between align-items-center mb-3">
                    <h4 class="text-md font-semibold m-0">
                      <i class="pi pi-building mr-2" aria-hidden="true"></i>
                      Titolare del Trattamento #{{ $index + 1 }}
                    </h4>
                  </div>

                  <div class="grid">
                    <div class="field col-12">
                      <label [for]="'controllerEntity-' + $index" class="font-semibold block mb-2">
                        Denominazione Entità <span class="text-red-500" aria-hidden="true">*</span>
                      </label>
                      <input
                        pInputText
                        [id]="'controllerEntity-' + $index"
                        [value]="getEntityLabel($any(controller).get('controllerEntity')?.value)"
                        readonly
                        disabled
                        aria-readonly="true"
                        class="w-full"
                        [attr.aria-required]="true"
                      />
                    </div>

                    <div class="field col-12 md:col-6">
                      <label
                        [for]="'controllerDpoEmail-' + $index"
                        class="font-semibold block mb-2"
                      >
                        DPO email <span class="text-red-500" aria-hidden="true">*</span>
                      </label>
                      <input
                        pInputText
                        type="email"
                        [id]="'controllerDpoEmail-' + $index"
                        formControlName="controllerDpoEmail"
                        class="w-full"
                        placeholder="es. dpo@axa.it"
                        [attr.aria-required]="true"
                        [attr.aria-invalid]="
                          $any(controller).get('controllerDpoEmail')?.invalid &&
                          $any(controller).get('controllerDpoEmail')?.touched
                        "
                        [attr.aria-describedby]="
                          $any(controller).get('controllerDpoEmail')?.invalid &&
                          $any(controller).get('controllerDpoEmail')?.touched
                            ? 'controllerDpoEmail-' + $index + '-error'
                            : null
                        "
                      />
                      @if (
                        $any(controller).get('controllerDpoEmail')?.invalid &&
                        $any(controller).get('controllerDpoEmail')?.touched
                      ) {
                        <div
                          [id]="'controllerDpoEmail-' + $index + '-error'"
                          class="mt-1"
                          role="alert"
                          aria-live="polite"
                        >
                          @if ($any(controller).get('controllerDpoEmail')?.hasError('required')) {
                            <small class="text-red-500 font-semibold block"
                              >❌ Il campo DPO email è obbligatorio.</small
                            >
                            <small class="text-gray-600 text-xs block mt-1"
                              >💡 Inserisci l'indirizzo email del DPO (es. dpo@axa.it).</small
                            >
                          } @else if ($any(controller).get('controllerDpoEmail')?.hasError('email')) {
                            <small class="text-red-500 font-semibold block"
                              >❌ Il formato dell'email non è valido.</small
                            >
                            <small class="text-gray-600 text-xs block mt-1"
                              >💡 Usa il formato: nome@dominio.it (es. dpo@axa.it).</small
                            >
                          }
                        </div>
                      }
                    </div>

                    <!-- Tipo titolarità -->
                    <div class="field col-12 md:col-6">
                      <label
                        [for]="'controllerOwnershipType-' + $index"
                        class="font-semibold block mb-2"
                      >
                        Tipo titolarità <span class="text-red-500" aria-hidden="true">*</span>
                      </label>
                      <p-select
                        [id]="'controllerOwnershipType-' + $index"
                        formControlName="controllerOwnershipType"
                        [options]="controllerOwnershipTypeOptions()"
                        optionLabel="valueDescription"
                        optionValue="valueKey"
                        placeholder="Seleziona tipo titolarità"
                        [showClear]="true"
                        class="w-full"
                        [attr.aria-required]="true"
                        [attr.aria-invalid]="
                          $any(controller).get('controllerOwnershipType')?.invalid &&
                          $any(controller).get('controllerOwnershipType')?.touched
                        "
                        [attr.aria-describedby]="
                          $any(controller).get('controllerOwnershipType')?.invalid &&
                          $any(controller).get('controllerOwnershipType')?.touched
                            ? 'controllerOwnershipType-' + $index + '-error'
                            : null
                        "
                      ></p-select>
                      @if (
                        $any(controller).get('controllerOwnershipType')?.invalid &&
                        $any(controller).get('controllerOwnershipType')?.touched
                      ) {
                        <small
                          [id]="'controllerOwnershipType-' + $index + '-error'"
                          class="text-red-500"
                          role="alert"
                          >Campo obbligatorio</small
                        >
                      }
                    </div>
                  </div>
                </div>
              </div>
            }

          </div>
        </section>

        <!-- SEZIONE: RESPONSABILE DEL TRATTAMENTO -->
        <section
          class="surface-0 border-round p-5 mb-4 shadow-1"
          role="region"
          aria-labelledby="responsabile-heading"
        >
          <div class="flex align-items-center gap-3 mb-4 pb-3 border-bottom-1 border-200">
            <i class="pi pi-user text-2xl" style="color: #00125b" aria-hidden="true"></i>
            <h2 id="responsabile-heading" class="text-xl font-semibold m-0" style="color: #00125b">
              Responsabile del Trattamento
            </h2>
          </div>

          <div class="grid">
            <div class="field col-12">
              <label class="font-semibold block mb-3">
                Ragione sociale del Responsabile <span class="text-red-500">*</span>
              </label>

              @if (isBackofficeProfessional()) {
                <div class="surface-100 border-round p-4 border-1 border-300">
                  <div class="flex align-items-start gap-3">
                    <i class="pi pi-building text-xl" style="color: #00125b" aria-hidden="true"></i>
                    <div class="flex-1">
                      <h4 class="text-lg font-semibold m-0 mb-2">
                        {{ step2Form.get('responsibleBusinessName')?.value || 'Non specificata' }}
                      </h4>
                      <div class="text-600 text-sm space-y-1">
                        <div class="flex align-items-center gap-2">
                          <i class="pi pi-briefcase" aria-hidden="true"></i>
                          <span>{{ decodeCompanyType(step2Form.get('companyType')?.value) || 'Non specificato' }}</span>
                        </div>
                        <div class="flex align-items-center gap-2">
                          <i class="pi pi-user" aria-hidden="true"></i>
                          <span>{{
                            step2Form.get('responsibleDpoName')?.value || 'Non specificato'
                          }}</span>
                        </div>
                        <div class="flex align-items-center gap-2">
                          <i class="pi pi-at" aria-hidden="true"></i>
                          <span>{{
                            step2Form.get('responsibleDpoEmail')?.value || 'Non specificata'
                          }}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div class="grid mt-3">
                  <div class="field col-12 md:col-6 mb-0">
                    <label for="responsibleBusinessName" class="font-semibold block mb-2">
                      Ragione sociale del Responsabile
                      <span class="text-red-500" aria-hidden="true">*</span>
                    </label>
                    <input
                      pInputText
                      id="responsibleBusinessName"
                      formControlName="responsibleBusinessName"
                      class="w-full"
                      placeholder="Inserisci ragione sociale"
                      [attr.aria-required]="true"
                      [attr.aria-invalid]="
                        step2Form.get('responsibleBusinessName')?.invalid &&
                        step2Form.get('responsibleBusinessName')?.touched
                      "
                    />
                    <small class="block mt-1 text-600">Non inserire il tipo di società</small>
                  </div>

                  <div class="field col-12 md:col-6 mb-0">
                    <label for="responsibleDpoName" class="font-semibold block mb-2">
                      Nome del DPO
                    </label>
                    <input
                      pInputText
                      id="responsibleDpoName"
                      formControlName="responsibleDpoName"
                      class="w-full"
                      placeholder="Inserisci nome del DPO"
                      [attr.aria-invalid]="
                        step2Form.get('responsibleDpoName')?.invalid &&
                        step2Form.get('responsibleDpoName')?.touched
                      "
                    />
                  </div>

                  @if (isSelectedEntityProfessional()) {
                    <div class="field col-12 md:col-6 mb-0">
                      <label for="responsibleDpoSurname" class="font-semibold block mb-2">
                        Cognome del DPO
                      </label>
                      <input
                        pInputText
                        id="responsibleDpoSurname"
                        formControlName="responsibleDpoSurname"
                        class="w-full"
                        placeholder="Inserisci cognome del DPO"
                      />
                    </div>
                  }
                </div>
              } @else {
                @if (!selectedResponsible()) {
                  <!-- Pulsante per aprire il dialog se non c'è selezione -->
                  <p-button
                    label="Seleziona dal Registro Responsabili"
                    icon="pi pi-search"
                    [outlined]="true"
                    styleClass="w-full justify-content-center"
                    (onClick)="openResponsibleDialog()"
                    ariaLabel="Seleziona responsabile dal registro"
                    title="Seleziona responsabile dal registro"
                  ></p-button>
                  <small class="text-muted block mt-2">
                    <i class="pi pi-info-circle mr-1" aria-hidden="true"></i>
                    Seleziona un responsabile dal registro o creane uno nuovo
                  </small>
                } @else {
                  <!-- Card del responsabile selezionato -->
                  <div class="surface-100 border-round p-4 border-1 border-300">
                    <div class="flex justify-content-between align-items-start mb-3">
                      <div class="flex-1">
                        <div class="flex align-items-center gap-2 mb-2">
                          <i
                            class="pi pi-building text-xl"
                            style="color: #00125b"
                            aria-hidden="true"
                          ></i>
                          <h4 class="text-lg font-semibold m-0">
                            {{ selectedResponsible().businessName }}
                          </h4>
                        </div>
                        <div class="text-600 text-sm space-y-1">
                          <div class="flex align-items-center gap-2">
                            <i class="pi pi-briefcase" aria-hidden="true"></i>
                            <span>{{ decodeCompanyType(selectedResponsible().companyType) || 'Non specificato' }}</span>
                          </div>
                          <div class="flex align-items-center gap-2">
                            <i class="pi pi-map-marker" aria-hidden="true"></i>
                            <span>{{
                              selectedResponsible().headquarters || 'Non specificata'
                            }}</span>
                          </div>
                          <div class="flex align-items-center gap-2">
                            <i class="pi pi-at" aria-hidden="true"></i>
                            <span>{{ selectedResponsible().email || 'Non specificata' }}</span>
                          </div>
                        </div>
                      </div>
                      <div class="flex gap-2">
                        <p-button
                          icon="pi pi-sync"
                          [text]="true"
                          [rounded]="true"
                          severity="secondary"
                          pTooltip="Sincronizza dal registro"
                          tooltipPosition="top"
                          (onClick)="syncResponsibleFromRegistry()"
                          [attr.aria-label]="'Sincronizza responsabile dal registro'"
                          title="Sincronizza responsabile dal registro"
                        ></p-button>
                        <p-button
                          icon="pi pi-times"
                          [text]="true"
                          [rounded]="true"
                          severity="danger"
                          pTooltip="Rimuovi selezione"
                          tooltipPosition="top"
                          (onClick)="clearResponsible()"
                          [attr.aria-label]="'Rimuovi responsabile selezionato'"
                          title="Rimuovi responsabile selezionato"
                        ></p-button>
                      </div>
                    </div>
                    <p-button
                      label="Cambia Responsabile"
                      icon="pi pi-pencil"
                      [outlined]="true"
                      size="small"
                      styleClass="w-full justify-content-center"
                      (onClick)="openResponsibleDialog()"
                      ariaLabel="Cambia responsabile selezionato"
                      title="Cambia responsabile selezionato"
                    ></p-button>
                  </div>
                }
              }

              <app-validation-error
                [control]="step2Form.get('responsibleBusinessName')"
                errorId="responsibleBusinessName-error"
                mode="strict"
              />

              @if (
                isBackofficeProfessional()
              ) {
                <app-validation-error
                  [control]="step2Form.get('responsibleDpoName')"
                  errorId="responsibleDpoName-error"
                  mode="name"
                />
              }
            </div>

            <div class="field col-12 md:col-6">
              <label for="companyType" class="font-semibold block mb-2">Tipo società</label>
              @if (isBackofficeProfessional()) {
                <p-select
                  id="companyType"
                  formControlName="companyType"
                  [options]="companyTypeOptions()"
                  optionLabel="valueDescription"
                  optionValue="valueKey"
                  optionDisabled="disabled"
                  [showClear]="true"
                  class="w-full"
                  placeholder="Seleziona tipo società"
                ></p-select>
              } @else {
                <input
                  pInputText
                  id="companyType"
                  class="w-full"
                  [value]="decodeCompanyType(step2Form.get('companyType')?.value)"
                  placeholder="Compilato automaticamente dal registro"
                  readonly
                />
              }
            </div>

            <div class="field col-12 md:col-6">
              <label for="headquarters" class="font-semibold block mb-2">
                Sede del responsabile
                @if (!isBackofficeProfessional()) {
                  <span class="text-red-500" aria-hidden="true">*</span>
                }
              </label>
              <input
                pInputText
                id="headquarters"
                formControlName="headquarters"
                class="w-full"
                [placeholder]="isBackofficeProfessional() ? 'es. Via Roma 1, 20121 Milano' : 'Compilato automaticamente dal registro'"
                [readonly]="!isBackofficeProfessional()"
                [attr.aria-required]="!isBackofficeProfessional() ? true : null"
                [attr.aria-invalid]="
                  step2Form.get('headquarters')?.invalid && step2Form.get('headquarters')?.touched
                "
                [attr.aria-describedby]="
                  step2Form.get('headquarters')?.invalid && step2Form.get('headquarters')?.touched
                    ? 'headquarters-error'
                    : null
                "
              />
              <app-validation-error
                [control]="step2Form.get('headquarters')"
                errorId="headquarters-error"
                mode="address"
              />
            </div>

            <div class="field col-12 md:col-6">
              <label for="responsibleDpoEmail" class="font-semibold block mb-2">
                Email DPO Responsabile <span class="text-red-500" aria-hidden="true">*</span>
              </label>
              <input
                pInputText
                type="email"
                id="responsibleDpoEmail"
                formControlName="responsibleDpoEmail"
                class="w-full"
                [placeholder]="!isSelectedEntityProfessional() ? 'Compilato automaticamente dal registro' : 'es. dpo@responsabile.it'"
                [readonly]="!isSelectedEntityProfessional()"
                [attr.aria-required]="true"
                [attr.aria-invalid]="
                  step2Form.get('responsibleDpoEmail')?.invalid &&
                  step2Form.get('responsibleDpoEmail')?.touched
                "
                [attr.aria-describedby]="
                  step2Form.get('responsibleDpoEmail')?.invalid &&
                  step2Form.get('responsibleDpoEmail')?.touched
                    ? 'responsibleDpoEmail-error'
                    : null
                "
              />
              <app-validation-error
                [control]="step2Form.get('responsibleDpoEmail')"
                errorId="responsibleDpoEmail-error"
                mode="strict"
              />
            </div>

            <div class="field col-12 md:col-6">
              <label for="processedDataTypes" class="font-semibold block mb-2">
                Dati trattati dal Responsabile
                <span class="text-red-500" aria-hidden="true">*</span>
              </label>
              <p-multiselect
                id="processedDataTypes"
                formControlName="processedDataTypes"
                [options]="processedDataTypesOptions()"
                optionLabel="valueDescription"
                optionValue="valueKey"
                placeholder="Seleziona tipologie"
                [showClear]="true"
                class="w-full"
                [attr.aria-required]="true"
                [attr.aria-invalid]="
                  step2Form.get('processedDataTypes')?.invalid &&
                  step2Form.get('processedDataTypes')?.touched
                "
                [attr.aria-describedby]="
                  step2Form.get('processedDataTypes')?.invalid &&
                  step2Form.get('processedDataTypes')?.touched
                    ? 'processedDataTypes-error'
                    : null
                "
              ></p-multiselect>
              @if (
                step2Form.get('processedDataTypes')?.invalid &&
                step2Form.get('processedDataTypes')?.touched
              ) {
                <small id="processedDataTypes-error" class="text-red-500" role="alert"
                  >Campo obbligatorio</small
                >
              }
            </div>

            @if (
              step2Form.get('processedDataTypes')?.value?.includes('OTHER') ||
              !isBackofficeProfessional()
            ) {
              <div class="field col-12">
                <label for="processedDataNotes" class="font-semibold block mb-2">
                  @if (step2Form.get('processedDataTypes')?.value?.includes('OTHER')) {
                    Specificare "Altro" dati trattati
                    <span class="text-red-500" aria-hidden="true">*</span>
                  } @else {
                    Note sui dati trattati
                  }
                </label>
                <textarea
                  pTextarea
                  id="processedDataNotes"
                  formControlName="processedDataNotes"
                  rows="2"
                  class="w-full"
                  [placeholder]="step2Form.get('processedDataTypes')?.value?.includes('OTHER')
                    ? 'Descrivi la tipologia di dato trattato'
                    : 'Specifica quali dati vengono trattati (opzionale)'"
                ></textarea>
                @if (
                  step2Form.get('processedDataNotes')?.invalid &&
                  step2Form.get('processedDataNotes')?.touched
                ) {
                  <small class="text-red-500">Campo obbligatorio</small>
                }
              </div>
            }

            <div class="field col-12" [hidden]="isBackofficeProfessional()">
              <label for="processDescription" class="font-semibold block mb-2">
                Descrizione sintetica del processo
                <span class="text-red-500" aria-hidden="true">*</span>
              </label>
              <textarea
                pTextarea
                id="processDescription"
                formControlName="processDescription"
                rows="2"
                class="w-full"
                placeholder="Preso dall'oggetto del contratto"
                [attr.aria-required]="true"
                [attr.aria-invalid]="
                  step2Form.get('processDescription')?.invalid &&
                  step2Form.get('processDescription')?.touched
                "
                [attr.aria-describedby]="
                  step2Form.get('processDescription')?.invalid &&
                  step2Form.get('processDescription')?.touched
                    ? 'processDescription-error'
                    : null
                "
              ></textarea>
              <app-validation-error
                [control]="step2Form.get('processDescription')"
                errorId="processDescription-error"
                mode="description"
              />
            </div>

            @if (!isBackofficeProfessional()) {
            <div class="field col-12 md:col-6">
              <label for="responsibleType" class="font-semibold block mb-2">
                Tipologia di Responsabile <span class="text-red-500" aria-hidden="true">*</span>
              </label>
              <p-select
                id="responsibleType"
                formControlName="responsibleType"
                [options]="responsibleTypeOptions()"
                optionLabel="valueDescription"
                optionValue="valueKey"
                placeholder="Seleziona tipologia"
                [showClear]="true"
                class="w-full"
                [attr.aria-required]="true"
                [attr.aria-invalid]="
                  step2Form.get('responsibleType')?.invalid &&
                  step2Form.get('responsibleType')?.touched
                "
                [attr.aria-describedby]="
                  step2Form.get('responsibleType')?.invalid &&
                  step2Form.get('responsibleType')?.touched
                    ? 'responsibleType-error'
                    : null
                "
              ></p-select>
              @if (
                step2Form.get('responsibleType')?.invalid &&
                step2Form.get('responsibleType')?.touched
              ) {
                <small id="responsibleType-error" class="text-red-500" role="alert"
                  >Campo obbligatorio</small
                >
              }
            </div>
            }

            @if (!isBackofficeProfessional()) {
            <div class="field col-12 md:col-6">
              <label for="dataProcessingLocation" class="font-semibold block mb-2">
                Luogo trattamento dati <span class="text-red-500" aria-hidden="true">*</span>
              </label>
              <p-select
                id="dataProcessingLocation"
                formControlName="dataProcessingLocation"
                [options]="luogoTrattamentoOptions()"
                optionLabel="valueDescription"
                optionValue="valueKey"
                placeholder="Seleziona paese"
                [filter]="true"
                filterPlaceholder="Cerca paese"
                [showClear]="true"
                class="w-full"
                [attr.aria-required]="true"
                [attr.aria-invalid]="
                  step2Form.get('dataProcessingLocation')?.invalid &&
                  step2Form.get('dataProcessingLocation')?.touched
                "
                [attr.aria-describedby]="
                  step2Form.get('dataProcessingLocation')?.invalid &&
                  step2Form.get('dataProcessingLocation')?.touched
                    ? 'dataProcessingLocation-error'
                    : null
                "
              ></p-select>
              <app-validation-error
                [control]="step2Form.get('dataProcessingLocation')"
                errorId="dataProcessingLocation-error"
                mode="address"
              />
            </div>
            }

            <div class="field col-12" [hidden]="isBackofficeProfessional()">
              <label for="externalSysAdminsText" class="font-semibold block mb-2">
                Amministratore di sistema
                <span class="text-red-500" aria-hidden="true">*</span>
              </label>
              <textarea
                pTextarea
                id="externalSysAdminsText"
                formControlName="externalSysAdminsText"
                rows="2"
                class="w-full"
                placeholder="Nominativi amministratori di sistema esterni"
                [attr.aria-required]="true"
                [attr.aria-invalid]="
                  step2Form.get('externalSysAdminsText')?.invalid &&
                  step2Form.get('externalSysAdminsText')?.touched
                "
                [attr.aria-describedby]="
                  step2Form.get('externalSysAdminsText')?.invalid &&
                  step2Form.get('externalSysAdminsText')?.touched
                    ? 'externalSysAdminsText-error'
                    : null
                "
              ></textarea>
              <app-validation-error
                [control]="step2Form.get('externalSysAdminsText')"
                errorId="externalSysAdminsText-error"
                mode="description"
              />
            </div>

            <div class="field col-12 md:col-6">
              <fieldset>
                <legend class="font-semibold block mb-3">
                  Trasferimento extra UE <span class="text-red-500" aria-hidden="true">*</span>
                </legend>
                <div class="flex gap-4" role="radiogroup" aria-label="Trasferimento extra UE">
                  @for (option of yesNoOptions; track option.value) {
                    <div class="flex align-items-center">
                      <p-radiobutton
                        name="transferOutsideEu"
                        [inputId]="'transferOutsideEu-' + option.value"
                        [value]="option.value"
                        formControlName="transferOutsideEu"
                        [attr.aria-required]="true"
                      ></p-radiobutton>
                      <label [for]="'transferOutsideEu-' + option.value" class="ml-2">{{
                        option.label
                      }}</label>
                    </div>
                  }
                </div>
              </fieldset>
            </div>

            @if (step2Form.get('transferOutsideEu')?.value) {
              <div class="field col-12 md:col-4">
                <label for="extraEuLegalBasis" class="font-semibold block mb-2">
                  Base legale trasferimento
                  @if (!isBackofficeProfessional()) {
                    <span class="text-red-500">*</span>
                  }
                </label>
                <input
                  pInputText
                  id="extraEuLegalBasis"
                  formControlName="extraEuLegalBasis"
                  class="w-full"
                  placeholder="es. Clausole contrattuali tipo"
                />
                @if (
                  step2Form.get('extraEuLegalBasis')?.invalid &&
                  step2Form.get('extraEuLegalBasis')?.touched
                ) {
                  <small class="text-red-500">Campo obbligatorio</small>
                }
              </div>

              <div class="field col-12 md:col-4">
                <label for="thirdCountry" class="font-semibold block mb-2">
                  Paese terzo del trasferimento
                  @if (!isBackofficeProfessional()) {
                    <span class="text-red-500">*</span>
                  }
                </label>
                <p-select
                  id="thirdCountry"
                  formControlName="thirdCountry"
                  [options]="nonEuCountriesOptions()"
                  optionLabel="valueDescription"
                  optionValue="valueKey"
                  placeholder="Seleziona paese extra UE"
                  [filter]="true"
                  filterPlaceholder="Cerca paese"
                  [showClear]="true"
                  class="w-full"
                ></p-select>
                @if (
                  step2Form.get('thirdCountry')?.invalid && step2Form.get('thirdCountry')?.touched
                ) {
                  <small class="text-red-500">Campo obbligatorio</small>
                }
              </div>

              <div class="field col-12 md:col-4">
                <label class="font-semibold block mb-3">
                  Effettuata TIA <span class="text-red-500">*</span>
                </label>
                <div class="flex gap-4">
                  @for (option of yesNoOptions; track option.value) {
                    <div class="flex align-items-center">
                      <p-radiobutton
                        name="tiaDone"
                        [inputId]="'tiaDone-' + option.value"
                        [value]="option.value"
                        formControlName="tiaDone"
                      ></p-radiobutton>
                      <label [for]="'tiaDone-' + option.value" class="ml-2">{{
                        option.label
                      }}</label>
                    </div>
                  }
                </div>
              </div>

              @if (step2Form.get('tiaDone')?.value === true) {
                <div class="field col-12">
                  <label for="tiaOutcome" class="font-semibold block mb-2">
                    Esito TIA <span class="text-red-500" aria-hidden="true">*</span>
                  </label>
                  <textarea
                    pInputTextarea
                    id="tiaOutcome"
                    formControlName="tiaOutcome"
                    rows="3"
                    class="w-full"
                    placeholder="Descrivi l'esito della TIA"
                    [attr.aria-required]="true"
                    [attr.aria-invalid]="
                      step2Form.get('tiaOutcome')?.invalid &&
                      step2Form.get('tiaOutcome')?.touched
                    "
                    [attr.aria-describedby]="
                      step2Form.get('tiaOutcome')?.invalid &&
                      step2Form.get('tiaOutcome')?.touched
                        ? 'tiaOutcome-error'
                        : null
                    "
                  ></textarea>
                  <app-validation-error
                    [control]="step2Form.get('tiaOutcome')"
                    errorId="tiaOutcome-error"
                    mode="strict"
                  />
                </div>
              }
            }

            @if (!isBackofficeProfessional()) {
            <div class="field col-12 md:col-6">
              <label class="font-semibold block mb-3">
                Programma di data deletion previsto <span class="text-red-500">*</span>
              </label>
              <div class="flex gap-4">
                @for (option of yesNoOptions; track option.value) {
                  <div class="flex align-items-center">
                    <p-radiobutton
                      name="dataDeletionProgram"
                      [inputId]="'dataDeletionProgram-' + option.value"
                      [value]="option.value"
                      formControlName="dataDeletionProgram"
                    ></p-radiobutton>
                    <label [for]="'dataDeletionProgram-' + option.value" class="ml-2">{{
                      option.label
                    }}</label>
                  </div>
                }
              </div>
            </div>
            }

            @if (!isBackofficeProfessional()) {
            <div class="field col-12 md:col-6">
              <label for="criticalSupplierQualification" class="font-semibold block mb-2">
                Qualificazione Fornitore Critico <span class="text-red-500">*</span>
              </label>
              <p-select
                id="criticalSupplierQualification"
                formControlName="criticalSupplierQualification"
                [options]="criticalSupplierQualificationOptions()"
                optionLabel="valueDescription"
                optionValue="valueKey"
                placeholder="Seleziona livello"
                [showClear]="true"
                class="w-full"
              ></p-select>
              @if (
                step2Form.get('criticalSupplierQualification')?.invalid &&
                step2Form.get('criticalSupplierQualification')?.touched
              ) {
                <small class="text-red-500">Campo obbligatorio</small>
              }
            </div>
            }

            <!-- Campi Intercompany e Contract-in rimossi -->

            <div class="field col-12">
              <label for="responsibleNotes" class="font-semibold block mb-2">
                Note per Registro Responsabili trattamento
              </label>
              <textarea
                pTextarea
                id="responsibleNotes"
                formControlName="notes"
                rows="3"
                class="w-full"
                placeholder="Note aggiuntive (opzionale)"
              ></textarea>
            </div>
          </div>
        </section>

        <!-- SEZIONE: SUB-RESPONSABILI DEL TRATTAMENTO -->
        <section
          class="surface-0 border-round p-5 mb-4 shadow-1"
          role="region"
          aria-labelledby="sub-responsabili-heading"
        >
          <div class="flex align-items-center gap-3 mb-4 pb-3 border-bottom-1 border-200">
            <i class="pi pi-sitemap text-2xl" style="color: #00125b" aria-hidden="true"></i>
            <h2
              id="sub-responsabili-heading"
              class="text-xl font-semibold m-0"
              style="color: #00125b"
            >
              Sub-Responsabili del Trattamento
            </h2>
          </div>

          <div class="field col-12">
            <label class="font-semibold block mb-3">
              Il Responsabile fa ricorso a Sub-Responsabili? <span class="text-red-500">*</span>
            </label>
            <div class="flex gap-4">
              @for (option of yesNoOptions; track option.value) {
                <div class="flex align-items-center">
                  <p-radiobutton
                    name="usesSubResponsibles"
                    [inputId]="'usesSubResponsibles-' + option.value"
                    [value]="option.value"
                    formControlName="usesSubResponsibles"
                  ></p-radiobutton>
                  <label [for]="'usesSubResponsibles-' + option.value" class="ml-2">{{
                    option.label
                  }}</label>
                </div>
              }
            </div>
          </div>

          @if (step2Form.get('usesSubResponsibles')?.value) {
            <div class="mt-4">
              <!-- Header senza pulsante -->
              <div class="mb-3">
                <h3 class="text-lg font-semibold m-0">Elenco Sub-Responsabili</h3>
                <small class="text-muted"
                  >Aggiungi uno o più sub-responsabili dal registro e completa le informazioni
                  richieste</small
                >
              </div>

              <!-- Messaggio informativo quando array vuoto -->
              @if (subResponsiblesArray.length === 0) {
                <p
                  class="text-center p-4 border-round mb-3"
                  role="status"
                  aria-live="polite"
                  [ngClass]="{
                    'text-muted surface-100': !(
                      step2Form.get('subResponsibles')?.invalid &&
                      step2Form.get('subResponsibles')?.touched
                    ),
                    'text-red-500 surface-red-50 border-1 border-red-500':
                      step2Form.get('subResponsibles')?.invalid &&
                      step2Form.get('subResponsibles')?.touched,
                  }"
                >
                  <i
                    class="pi mr-2"
                    aria-hidden="true"
                    [ngClass]="{
                      'pi-info-circle': !(
                        step2Form.get('subResponsibles')?.invalid &&
                        step2Form.get('subResponsibles')?.touched
                      ),
                      'pi-exclamation-triangle':
                        step2Form.get('subResponsibles')?.invalid &&
                        step2Form.get('subResponsibles')?.touched,
                    }"
                  ></i>
                  Nessun sub-responsabile aggiunto. Clicca "Aggiungi Sub-Responsabile" per iniziare.
                </p>
                @if (
                  step2Form.get('subResponsibles')?.invalid &&
                  step2Form.get('subResponsibles')?.touched
                ) {
                  <small class="text-red-500 block text-center mb-3">
                    <i class="pi pi-exclamation-circle mr-1" aria-hidden="true"></i>
                    È obbligatorio aggiungere almeno un Sub-Responsabile del Trattamento
                  </small>
                }
              }

              @for (sub of subResponsiblesArray.controls; track $index) {
                <div [formGroup]="$any(sub)" class="surface-0 border-round p-4 mb-3 shadow-1">
                  <!-- Header Card con numerazione -->
                  <div class="surface-100 border-round p-4 border-1 border-300 mb-4">
                    <div class="flex justify-content-between align-items-start">
                      <div class="flex-1">
                        <div class="flex align-items-center gap-2 mb-2">
                          <i class="pi pi-sitemap text-xl" style="color: #00125b"></i>
                          <h4 class="text-lg font-semibold m-0">
                            Sub-Responsabile #{{ $index + 1 }}
                            @if ($any(sub).get('businessName')?.value) {
                              <span class="font-normal text-600">
                                - {{ $any(sub).get('businessName')?.value }}</span
                              >
                            }
                          </h4>
                        </div>
                        <div class="text-600 text-sm space-y-1">
                          <div class="flex align-items-center gap-2 mb-1">
                            <i class="pi pi-briefcase" aria-hidden="true"></i>
                            <span>{{ decodeCompanyType($any(sub).get('companyType')?.value) || 'Non specificato' }}</span>
                          </div>
                          <div class="flex align-items-center gap-2 mb-1">
                            <i class="pi pi-map-marker" aria-hidden="true"></i>
                            <span>{{
                              $any(sub).get('headquarters')?.value || 'Non specificata'
                            }}</span>
                          </div>
                          <div class="flex align-items-center gap-2">
                            <i class="pi pi-at" aria-hidden="true"></i>
                            <span>{{ $any(sub).get('email')?.value || 'Non specificata' }}</span>
                          </div>
                        </div>
                      </div>
                      <div class="flex gap-2">
                        @if (!isBackofficeProfessional()) {
                          <p-button
                            icon="pi pi-sync"
                            [text]="true"
                            [rounded]="true"
                            severity="secondary"
                            pTooltip="Sincronizza dal registro"
                            tooltipPosition="top"
                            (onClick)="syncSubResponsibleFromRegistry($index)"
                            [attr.aria-label]="
                              'Sincronizza sub-responsabile #' + ($index + 1) + ' dal registro'
                            "
                            [title]="
                              'Sincronizza sub-responsabile #' + ($index + 1) + ' dal registro'
                            "
                          ></p-button>
                        }
                        <p-button
                          icon="pi pi-trash"
                          [text]="true"
                          [rounded]="true"
                          severity="danger"
                          pTooltip="Rimuovi sub-responsabile"
                          tooltipPosition="top"
                          (onClick)="removeSubResponsible($index)"
                          [ariaLabel]="'Rimuovi sub-responsabile #' + ($index + 1)"
                          [title]="'Rimuovi sub-responsabile #' + ($index + 1)"
                        ></p-button>
                      </div>
                    </div>
                    @if (!isBackofficeProfessional()) {
                      <p-button
                        label="Cambia Sub-Responsabile"
                        icon="pi pi-pencil"
                        [outlined]="true"
                        size="small"
                        styleClass="w-full justify-content-center"
                        (onClick)="openSubResponsibleChangeDialog($index)"
                        [ariaLabel]="'Cambia sub-responsabile #' + ($index + 1)"
                        [title]="'Cambia sub-responsabile #' + ($index + 1)"
                      ></p-button>
                    }
                  </div>

                  <!-- Campi Editabili -->
                  <div class="grid">
                    <!-- Campi dal Registro - Read Only -->
                    <div class="field col-12 md:col-4">
                      <label [for]="'businessName-' + $index" class="font-semibold block mb-2">
                        Ragione sociale <span class="text-red-500">*</span>
                      </label>
                      <input
                        pInputText
                        [id]="'businessName-' + $index"
                        formControlName="businessName"
                        class="w-full"
                        [readonly]="!isBackofficeProfessional()"
                        placeholder="Compilato automaticamente dal registro"
                      />
                      @if (isBackofficeProfessional()) {
                        <small class="block mt-1 text-600">Non inserire il tipo di società</small>
                      }
                    </div>

                    <div class="field col-12 md:col-4">
                      <label [for]="'companyType-' + $index" class="font-semibold block mb-2">
                        Tipo società
                      </label>
                      @if (isBackofficeProfessional()) {
                        <p-select
                          [id]="'companyType-' + $index"
                          formControlName="companyType"
                          [options]="companyTypeOptions()"
                          optionLabel="valueDescription"
                          optionValue="valueKey"
                          [showClear]="true"
                          class="w-full"
                          placeholder="Seleziona tipo società"
                        ></p-select>
                      } @else {
                        <input
                          pInputText
                          [id]="'companyType-' + $index"
                          class="w-full"
                          [value]="decodeCompanyType($any(sub).get('companyType')?.value)"
                          placeholder="Compilato automaticamente dal registro"
                          readonly
                        />
                      }
                    </div>

                    <div class="field col-12 md:col-4">
                      <label [for]="'headquarters-' + $index" class="font-semibold block mb-2">
                        Sede legale <span class="text-red-500">*</span>
                      </label>
                      <input
                        pInputText
                        [id]="'headquarters-' + $index"
                        formControlName="headquarters"
                        class="w-full"
                        [readonly]="!isBackofficeProfessional()"
                        placeholder="Compilato automaticamente dal registro"
                      />
                    </div>

                    <div class="field col-12 md:col-4">
                      <label [for]="'email-' + $index" class="font-semibold block mb-2">
                        Email
                      </label>
                      <input
                        pInputText
                        [id]="'email-' + $index"
                        formControlName="email"
                        class="w-full"
                        [readonly]="!isBackofficeProfessional()"
                        placeholder="es. email@subresponsabile.it"
                      />
                    </div>

                    <div class="field col-12 md:col-6">
                      <label
                        [for]="'dataProcessingLocation-' + $index"
                        class="font-semibold block mb-2"
                      >
                        Luogo trattamento dati <span class="text-red-500">*</span>
                      </label>
                      <p-select
                        [id]="'dataProcessingLocation-' + $index"
                        formControlName="dataProcessingLocation"
                        [options]="subDataProcessingLocationOptions()"
                        optionLabel="valueDescription"
                        optionValue="valueKey"
                        placeholder="Seleziona luogo"
                        [showClear]="true"
                        class="w-full"
                      ></p-select>
                      @if (
                        $any(sub).get('dataProcessingLocation')?.invalid &&
                        $any(sub).get('dataProcessingLocation')?.touched
                      ) {
                        <small class="text-red-500">Campo obbligatorio</small>
                      }
                    </div>

                    @if ($any(sub).get('dataProcessingLocation')?.value === 'UE' || $any(sub).get('dataProcessingLocation')?.value === 'EXTRA_UE') {
                      <div class="field col-12 md:col-6">
                        <label [for]="'dataProcessingCountry-' + $index" class="font-semibold block mb-2">
                          Paese trattamento dati <span class="text-red-500">*</span>
                        </label>
                        <p-select
                          [id]="'dataProcessingCountry-' + $index"
                          formControlName="dataProcessingCountry"
                          [options]="luogoTrattamentoOptions()"
                          optionLabel="valueDescription"
                          optionValue="valueKey"
                          placeholder="Seleziona paese"
                          [filter]="true"
                          filterPlaceholder="Cerca paese"
                          [showClear]="true"
                          class="w-full"
                        ></p-select>
                        @if (
                          $any(sub).get('dataProcessingCountry')?.invalid &&
                          $any(sub).get('dataProcessingCountry')?.touched
                        ) {
                          <small class="text-red-500">Campo obbligatorio</small>
                        }
                      </div>
                    }

                    @if ($any(sub).get('dataProcessingLocation')?.value === 'EXTRA_UE') {
                      <div class="field col-12">
                        <label [for]="'nonEuSafeguard-' + $index" class="font-semibold block mb-2">
                          Garanzie adottate per trasferimento extra UE
                          <span class="text-red-500">*</span>
                        </label>
                        <textarea
                          pTextarea
                          [id]="'nonEuSafeguard-' + $index"
                          formControlName="nonEuSafeguard"
                          rows="2"
                          class="w-full"
                          placeholder="Descrivere le garanzie"
                        ></textarea>
                        @if (
                          $any(sub).get('nonEuSafeguard')?.invalid &&
                          $any(sub).get('nonEuSafeguard')?.touched
                        ) {
                          <small class="text-red-500">Campo obbligatorio</small>
                        }
                      </div>
                    }

                    <div class="field col-12">
                      <label [for]="'dataTypes-' + $index" class="font-semibold block mb-2">
                        Tipo dati trattati <span class="text-red-500">*</span>
                      </label>
                      <p-multiselect
                        [id]="'dataTypes-' + $index"
                        formControlName="dataTypes"
                        [options]="processedDataTypesOptions()"
                        optionLabel="valueDescription"
                        optionValue="valueKey"
                        placeholder="Seleziona tipologie"
                        [showClear]="true"
                        class="w-full"
                      ></p-multiselect>
                      @if (
                        $any(sub).get('dataTypes')?.invalid && $any(sub).get('dataTypes')?.touched
                      ) {
                        <small class="text-red-500">Campo obbligatorio</small>
                      }
                    </div>

                    @if (
                      $any(sub).get('dataTypes')?.value?.includes('OTHER') ||
                      !isBackofficeProfessional()
                    ) {
                      <div class="field col-12">
                        <label [for]="'dataTypesNotes-' + $index" class="font-semibold block mb-2">
                          @if ($any(sub).get('dataTypes')?.value?.includes('OTHER')) {
                            Specificare "Altro" dati trattati
                            <span class="text-red-500" aria-hidden="true">*</span>
                          } @else {
                            Note sui dati trattati
                          }
                        </label>
                        <textarea
                          pTextarea
                          [id]="'dataTypesNotes-' + $index"
                          formControlName="dataTypesNotes"
                          rows="2"
                          class="w-full"
                          [placeholder]="$any(sub).get('dataTypes')?.value?.includes('OTHER')
                            ? 'Descrivi la tipologia di dato trattato'
                            : 'Specifica quali dati vengono trattati (opzionale)'"
                        ></textarea>
                        @if (
                          $any(sub).get('dataTypesNotes')?.invalid &&
                          $any(sub).get('dataTypesNotes')?.touched
                        ) {
                          <small class="text-red-500">Campo obbligatorio</small>
                        }
                      </div>
                    }

                    <div class="field col-12">
                      <label
                        [for]="'delegatedProcessesDescription-' + $index"
                        class="font-semibold block mb-2"
                      >
                        Descrizione processi affidati <span class="text-red-500">*</span>
                      </label>
                      <textarea
                        pTextarea
                        [id]="'delegatedProcessesDescription-' + $index"
                        formControlName="delegatedProcessesDescription"
                        rows="3"
                        class="w-full"
                        placeholder="Descrivere i processi affidati al sub-responsabile"
                      ></textarea>
                      @if (
                        $any(sub).get('delegatedProcessesDescription')?.invalid &&
                        $any(sub).get('delegatedProcessesDescription')?.touched
                      ) {
                        <small class="text-red-500">Campo obbligatorio</small>
                      }
                    </div>

                    <div class="field col-12">
                      <label [for]="'notes-' + $index" class="font-semibold block mb-2">
                        Note per Registro Sub Responsabili trattamento
                      </label>
                      <textarea
                        pTextarea
                        [id]="'notes-' + $index"
                        formControlName="notes"
                        rows="2"
                        class="w-full"
                        placeholder="Note aggiuntive (opzionale)"
                      ></textarea>
                    </div>
                  </div>
                </div>
              }

              <!-- Pulsante Aggiungi in basso a destra -->
              <div class="flex justify-content-end">
                <p-button
                  label="Aggiungi Sub-Responsabile"
                  icon="pi pi-plus"
                  size="small"
                  (onClick)="
                    isBackofficeProfessional()
                      ? addSubResponsible()
                      : openSubResponsibleSelectionDialog()
                  "
                  ariaLabel="Aggiungi sub-responsabile"
                  title="Aggiungi sub-responsabile"
                ></p-button>
              </div>
            </div>
          }
        </section>

        <!-- SEZIONE: OPERAZIONI DI TRATTAMENTO -->
        <section
          class="surface-0 border-round p-5 mb-4 shadow-1"
          role="region"
          aria-labelledby="operazioni-heading"
        >
          <div class="flex align-items-center gap-3 mb-4 pb-3 border-bottom-1 border-200">
            <i class="pi pi-cog text-2xl" style="color: #00125b" aria-hidden="true"></i>
            <h2 id="operazioni-heading" class="text-xl font-semibold m-0" style="color: #00125b">
              Operazioni di Trattamento
            </h2>
          </div>

          <div class="field col-12">
            <label for="activityDescription" class="font-semibold block mb-2">
              Specificare brevemente le attività pertinenti al trattamento
              <span class="text-red-500">*</span>
            </label>
            @if (isBackofficeProfessional()) {
              <p-multiselect
                id="activityDescription"
                formControlName="activityDescription"
                [options]="activityDescriptionOptions()"
                optionLabel="valueDescription"
                optionValue="valueKey"
                placeholder="Seleziona operazioni"
                [showClear]="true"
                class="w-full"
              ></p-multiselect>
              @if (step2Form.get('activityDescription')?.value?.includes('OTHER')) {
                <div class="mt-3">
                  <label for="activityDescriptionOther" class="font-semibold block mb-2">
                    Specificare l'attività di trattamento
                    <span class="text-red-500">*</span>
                  </label>
                  <input
                    pInputText
                    id="activityDescriptionOther"
                    formControlName="activityDescriptionOther"
                    class="w-full"
                    placeholder="Descrivi l'attività di trattamento"
                  />
                  @if (
                    step2Form.get('activityDescriptionOther')?.invalid &&
                    step2Form.get('activityDescriptionOther')?.touched
                  ) {
                    <small class="text-red-500">Campo obbligatorio</small>
                  }
                </div>
              }
            } @else {
              <textarea
                pTextarea
                id="activityDescription"
                formControlName="activityDescription"
                rows="3"
                class="w-full"
                placeholder="es. Gestione pagamenti, assistenza clienti, analisi dati"
              ></textarea>
            }
            @if (
              step2Form.get('activityDescription')?.invalid &&
              step2Form.get('activityDescription')?.touched
            ) {
              <small class="text-red-500">Campo obbligatorio</small>
            }
          </div>
        </section>

        <!-- SEZIONE: FINALITÀ DEL TRATTAMENTO -->
        <section
          class="surface-0 border-round p-5 mb-4 shadow-1"
          role="region"
          aria-labelledby="finalita-heading"
        >
          <div class="flex align-items-center gap-3 mb-4 pb-3 border-bottom-1 border-200">
            <i class="pi pi-flag text-2xl" style="color: #00125b" aria-hidden="true"></i>
            <h2 id="finalita-heading" class="text-xl font-semibold m-0" style="color: #00125b">
              Finalità del Trattamento
            </h2>
          </div>

          <div class="field col-12">
            <label for="mainPurpose" class="font-semibold block mb-2">
              Specificare brevemente lo scopo o gli scopi di trattamento dei dati
              <span class="text-red-500">*</span>
            </label>
            @if (isBackofficeProfessional()) {
              <p-select
                id="mainPurpose"
                formControlName="mainPurpose"
                [options]="mainPurposeOptions()"
                optionLabel="valueDescription"
                optionValue="valueKey"
                placeholder="Seleziona finalità"
                [showClear]="true"
                class="w-full"
              ></p-select>

              @if (step2Form.get('mainPurpose')?.value === 'OTHER') {
                <div class="mt-3">
                  <label for="mainPurposeOther" class="font-semibold block mb-2">
                    Specificare lo scopo del trattamento
                    <span class="text-red-500">*</span>
                  </label>
                  <input
                    pInputText
                    id="mainPurposeOther"
                    formControlName="mainPurposeOther"
                    class="w-full"
                    placeholder="Inserisci lo scopo del trattamento"
                  />
                  @if (
                    step2Form.get('mainPurposeOther')?.invalid &&
                    step2Form.get('mainPurposeOther')?.touched
                  ) {
                    <small class="text-red-500">Campo obbligatorio</small>
                  }
                </div>
              }
            } @else {
              <textarea
                pTextarea
                id="mainPurpose"
                formControlName="mainPurpose"
                rows="3"
                class="w-full"
                placeholder="es. Erogazione servizi, gestione contratti, adempimenti fiscali"
              ></textarea>
            }
            @if (step2Form.get('mainPurpose')?.invalid && step2Form.get('mainPurpose')?.touched) {
              <small class="text-red-500">Campo obbligatorio</small>
            }
          </div>
        </section>

        <!-- SEZIONE: CATEGORIE DI SOGGETTI -->
        <section
          class="surface-0 border-round p-5 mb-4 shadow-1"
          role="region"
          aria-labelledby="categorie-heading"
        >
          <div class="flex align-items-center gap-3 mb-4 pb-3 border-bottom-1 border-200">
            <i class="pi pi-users text-2xl" style="color: #00125b" aria-hidden="true"></i>
            <h2 id="categorie-heading" class="text-xl font-semibold m-0" style="color: #00125b">
              Categorie di Soggetti
            </h2>
          </div>

          <div class="field col-12">
            <label for="category" class="font-semibold block mb-2">
              Categorie di persone fisiche i cui dati sono oggetto di trattamento
              <span class="text-red-500">*</span>
            </label>
            <p-multiselect
              id="category"
              formControlName="category"
              [options]="subjectCategoryOptions()"
              optionLabel="valueDescription"
              optionValue="valueKey"
              placeholder="Seleziona categorie (multiplo)"
              [showClear]="true"
              class="w-full"
            ></p-multiselect>
            @if (step2Form.get('category')?.invalid && step2Form.get('category')?.touched) {
              <small class="text-red-500">Campo obbligatorio</small>
            }
          </div>

          @if (step2Form.get('category')?.value?.includes('OTHER')) {
            <div class="field col-12">
              <label for="categoryOther" class="font-semibold block mb-2">
                Specificare "Altro" per le categorie di persone fisiche
                <span class="text-red-500">*</span>
              </label>
              <textarea
                pTextarea
                id="categoryOther"
                formControlName="categoryOther"
                rows="2"
                class="w-full"
                placeholder="Specifica la categoria non presente in elenco"
                [attr.aria-required]="true"
                [attr.aria-invalid]="
                  step2Form.get('categoryOther')?.invalid &&
                  step2Form.get('categoryOther')?.touched
                "
                [attr.aria-describedby]="
                  step2Form.get('categoryOther')?.invalid &&
                  step2Form.get('categoryOther')?.touched
                    ? 'categoryOther-error'
                    : null
                "
              ></textarea>
              @if (
                step2Form.get('categoryOther')?.invalid &&
                step2Form.get('categoryOther')?.touched
              ) {
                <small id="categoryOther-error" class="text-red-500" role="alert"
                  >Campo obbligatorio</small
                >
              }
            </div>
          }
        </section>

        <!-- SEZIONE: DESTINATARI -->
        <section
          class="surface-0 border-round p-5 mb-4 shadow-1"
          role="region"
          aria-labelledby="destinatari-heading"
        >
          <div class="flex align-items-center gap-3 mb-4 pb-3 border-bottom-1 border-200">
            <i class="pi pi-send text-2xl" style="color: #00125b" aria-hidden="true"></i>
            <h2 id="destinatari-heading" class="text-xl font-semibold m-0" style="color: #00125b">
              Destinatari UE/extra UE
            </h2>
          </div>

          <div class="field col-12">
            <label for="recipientCategory" class="font-semibold block mb-2">
              Specificare brevemente le categorie dei soggetti a cui i dati sono comunicati
              <span class="text-red-500">*</span>
            </label>
            <p-multiselect
              id="recipientCategory"
              formControlName="recipientCategory"
              [options]="recipientCategoryOptions()"
              optionLabel="valueDescription"
              optionValue="valueKey"
              placeholder="Seleziona categorie (multiplo)"
              [showClear]="true"
              class="w-full"
            ></p-multiselect>
            @if (
              step2Form.get('recipientCategory')?.invalid &&
              step2Form.get('recipientCategory')?.touched
            ) {
              <small class="text-red-500">Campo obbligatorio</small>
            }
          </div>

          @if (step2Form.get('recipientCategory')?.value?.includes('OTHER')) {
            <div class="field col-12">
              <label for="recipientCategoryOther" class="font-semibold block mb-2">
                Specificare "Altro" per i destinatari UE/extra UE
                <span class="text-red-500">*</span>
              </label>
              <textarea
                pTextarea
                id="recipientCategoryOther"
                formControlName="recipientCategoryOther"
                rows="2"
                class="w-full"
                placeholder="Specifica il destinatario non presente in elenco"
                [attr.aria-required]="true"
                [attr.aria-invalid]="
                  step2Form.get('recipientCategoryOther')?.invalid &&
                  step2Form.get('recipientCategoryOther')?.touched
                "
                [attr.aria-describedby]="
                  step2Form.get('recipientCategoryOther')?.invalid &&
                  step2Form.get('recipientCategoryOther')?.touched
                    ? 'recipientCategoryOther-error'
                    : null
                "
              ></textarea>
              @if (
                step2Form.get('recipientCategoryOther')?.invalid &&
                step2Form.get('recipientCategoryOther')?.touched
              ) {
                <small id="recipientCategoryOther-error" class="text-red-500" role="alert"
                  >Campo obbligatorio</small
                >
              }
            </div>
          }
        </section>

        <!-- SEZIONE: CONSERVAZIONE E CANCELLAZIONE -->
        <section
          class="surface-0 border-round p-5 mb-4 shadow-1"
          role="region"
          aria-labelledby="conservazione-heading"
        >
          <div class="flex align-items-center gap-3 mb-4 pb-3 border-bottom-1 border-200">
            <i class="pi pi-calendar text-2xl" style="color: #00125b" aria-hidden="true"></i>
            <h2 id="conservazione-heading" class="text-xl font-semibold m-0" style="color: #00125b">
              Conservazione e Cancellazione
            </h2>
          </div>

          <div class="grid">
            <div class="field col-12 md:col-4">
              <label for="retentionPeriod" class="font-semibold block mb-2">
                Periodo di conservazione
                @if (!isBackofficeProfessional()) { <span class="text-red-500">*</span> }
              </label>
              <p-inputnumber
                id="retentionPeriod"
                formControlName="retentionPeriod"
                suffix=" anni"
                [min]="0"
                placeholder="es. 10"
                class="w-full"
              ></p-inputnumber>
              @if (
                step2Form.get('retentionPeriod')?.invalid &&
                step2Form.get('retentionPeriod')?.touched
              ) {
                <small class="text-red-500">Campo obbligatorio</small>
              }
            </div>

            <div class="field col-12 md:col-4" [hidden]="isBackofficeProfessional()">
              <label for="retentionCriteria" class="font-semibold block mb-2">
                Criteri di determinazione
              </label>
              <input
                pInputText
                id="retentionCriteria"
                formControlName="retentionCriteria"
                class="w-full"
                placeholder="es. Scadenza contratto, obblighi di legge"
              />
            </div>

            <div class="field col-12 md:col-4">
              <label for="deletionMethod" class="font-semibold block mb-2">
                Modalità di cancellazione <span class="text-red-500">*</span>
              </label>
              @if (isBackofficeProfessional()) {
                <p-select
                  id="deletionMethod"
                  formControlName="deletionMethod"
                  [options]="deletionMethodOptions()"
                  optionLabel="valueDescription"
                  optionValue="valueKey"
                  placeholder="Seleziona modalità"
                  [showClear]="true"
                  class="w-full"
                  [attr.aria-required]="true"
                  [attr.aria-invalid]="
                    step2Form.get('deletionMethod')?.invalid &&
                    step2Form.get('deletionMethod')?.touched
                  "
                  [attr.aria-describedby]="
                    step2Form.get('deletionMethod')?.invalid &&
                    step2Form.get('deletionMethod')?.touched
                      ? 'deletionMethod-error'
                      : null
                  "
                ></p-select>
              } @else {
                <input
                  pInputText
                  id="deletionMethod"
                  formControlName="deletionMethod"
                  class="w-full"
                  placeholder="es. Cancellazione fisica, anonimizzazione"
                />
              }
              @if (
                step2Form.get('deletionMethod')?.invalid && step2Form.get('deletionMethod')?.touched
              ) {
                <small id="deletionMethod-error" class="text-red-500">Campo obbligatorio</small>
              }
            </div>
          </div>
        </section>

        <!-- SEZIONE: STRUMENTI UTILIZZATI -->
        <section
          class="surface-0 border-round p-5 mb-4 shadow-1"
          role="region"
          aria-labelledby="strumenti-heading"
        >
          <div class="flex align-items-center gap-3 mb-4 pb-3 border-bottom-1 border-200">
            <i class="pi pi-wrench text-2xl" style="color: #00125b" aria-hidden="true"></i>
            <h2 id="strumenti-heading" class="text-xl font-semibold m-0" style="color: #00125b">
              Strumenti Utilizzati
            </h2>
          </div>

          <div class="field col-12">
            <label for="otherTools" class="font-semibold block mb-2">
              Specificare brevemente gli strumenti utilizzati per il trattamento
              <span class="text-red-500">*</span>
            </label>
            <textarea
              pTextarea
              id="otherTools"
              formControlName="otherTools"
              rows="3"
              class="w-full"
              placeholder="es. SAP, Salesforce, Microsoft Azure"
            ></textarea>
            @if (step2Form.get('otherTools')?.invalid && step2Form.get('otherTools')?.touched) {
              <small class="text-red-500">Campo obbligatorio</small>
            }
          </div>
        </section>

        <!-- SEZIONE: MISURE DI SICUREZZA -->
        <section
          class="surface-0 border-round p-5 mb-4 shadow-1"
          role="region"
          aria-labelledby="misure-heading"
        >
          <div class="flex align-items-center gap-3 mb-4 pb-3 border-bottom-1 border-200">
            <i class="pi pi-shield text-2xl" style="color: #00125b" aria-hidden="true"></i>
            <h2 id="misure-heading" class="text-xl font-semibold m-0" style="color: #00125b">
              Misure di Sicurezza
            </h2>
          </div>

          <div class="field col-12">
            <label for="measure" class="font-semibold block mb-2">
              Descrivere le misure tecniche e organizzative di sicurezza attuate
              <span class="text-red-500">*</span>
            </label>
            <textarea
              pTextarea
              id="measure"
              formControlName="measure"
              rows="4"
              class="w-full"
              placeholder="es. Crittografia, controllo accessi, backup giornalieri, firewall, VPN"
            ></textarea>
            @if (step2Form.get('measure')?.invalid && step2Form.get('measure')?.touched) {
              <small class="text-red-500">Campo obbligatorio</small>
            }
          </div>
        </section>

        <!-- SEZIONE: AMMINISTRATORI DI SISTEMA -->
        <section
          class="surface-0 border-round p-5 mb-4 shadow-1"
          role="region"
          aria-labelledby="amministratori-heading"
        >
          <div class="flex align-items-center gap-3 mb-4 pb-3 border-bottom-1 border-200">
            <i class="pi pi-user-edit text-2xl" style="color: #00125b" aria-hidden="true"></i>
            <h2
              id="amministratori-heading"
              class="text-xl font-semibold m-0"
              style="color: #00125b"
            >
              Amministratori di Sistema
            </h2>
          </div>

          <div class="field col-12">
            <label for="adminsText" class="font-semibold block mb-2">
              Elencare gli Amministratori di Sistema preposti o specificare se presente elenco da
              mettere a disposizione su richiesta <span class="text-red-500">*</span>
            </label>
            <textarea
              pTextarea
              id="adminsText"
              formControlName="adminsText"
              rows="3"
              class="w-full"
              placeholder="es. Mario Rossi, Giuseppe Bianchi o 'Elenco disponibile su richiesta del Titolare'"
            ></textarea>
            @if (step2Form.get('adminsText')?.invalid && step2Form.get('adminsText')?.touched) {
              <small class="text-red-500">Campo obbligatorio</small>
            }
          </div>
        </section>

        <!-- SEZIONE: INFORMAZIONI SUL CLOUD -->
        <section
          class="surface-0 border-round p-5 mb-4 shadow-1"
          role="region"
          aria-labelledby="cloud-heading"
        >
          <div class="flex align-items-center gap-3 mb-4 pb-3 border-bottom-1 border-200">
            <i class="pi pi-cloud text-2xl" style="color: #00125b" aria-hidden="true"></i>
            <h2 id="cloud-heading" class="text-xl font-semibold m-0" style="color: #00125b">
              Informazioni sul Cloud
            </h2>
          </div>

          <!-- Indicare se il Responsabile utilizza il Cloud -->
          <div class="field col-12">
            <label class="font-semibold block mb-3">
              Indicare se il Responsabile utilizza il Cloud
              <span class="text-red-500">*</span>
            </label>
            <div
              class="flex gap-4"
              role="radiogroup"
              aria-label="Indicare se il Responsabile utilizza il Cloud"
            >
              <div class="flex align-items-center">
                <p-radiobutton
                  name="usesCloud"
                  inputId="uses-cloud-yes"
                  [value]="true"
                  formControlName="usesCloud"
                ></p-radiobutton>
                <label for="uses-cloud-yes" class="ml-2">Sì</label>
              </div>
              <div class="flex align-items-center">
                <p-radiobutton
                  name="usesCloud"
                  inputId="uses-cloud-no"
                  [value]="false"
                  formControlName="usesCloud"
                ></p-radiobutton>
                <label for="uses-cloud-no" class="ml-2">No</label>
              </div>
            </div>
          </div>

          <!-- Tipologia di Cloud (visibile solo se usesCloud = true) -->
          @if (step2Form.get('usesCloud')?.value === true) {
            <div class="field col-12">
              <label for="cloudInfo" class="font-semibold block mb-2">
                Indicare la tipologia di Cloud utilizzato per il trattamento dei dati
                <span class="text-red-500">*</span>
              </label>
              <textarea
                pTextarea
                id="cloudInfo"
                formControlName="cloudInfo"
                rows="2"
                class="w-full"
                placeholder="es. Cloud AWS, cloud pubblico, PaaS, IaaS, SaaS"
              ></textarea>
              @if (step2Form.get('cloudInfo')?.invalid && step2Form.get('cloudInfo')?.touched) {
                <small class="text-red-500">Campo obbligatorio</small>
              }
            </div>
          }
        </section>

        <!-- Action Buttons -->
        <div class="flex flex-wrap justify-content-between gap-3 mt-4">
          <p-button
            label="Indietro"
            icon="pi pi-arrow-left"
            severity="secondary"
            [outlined]="true"
            (onClick)="goToPreviousStep()"
            title="Torna al passo precedente"
          ></p-button>
          <div class="flex flex-wrap gap-3">
            <p-button
              label="Salva Bozza"
              icon="pi pi-save"
              [outlined]="true"
              styleClass="btn-draft"
              (onClick)="saveDraft(2)"
              aria-label="Salva come bozza senza validazione"
              title="Salva come bozza senza validazione"
            ></p-button>
            <p-button
              label="Salva e Prosegui"
              icon="pi pi-arrow-right"
              iconPos="right"
              type="submit"
              title="Salva e prosegui al passo successivo"
            ></p-button>
          </div>
        </div>
      </form>
    }

    <!-- ================= STEP GESTIONE DOCUMENTI (3 per Supplier, 4 per Professional) ================= -->
    @if ((currentStep() === 3 && !isBackofficeProfessional()) || (currentStep() === 4 && isBackofficeProfessional())) {
      <!-- Breadcrumb -->
      <div class="mb-4">
        <p-breadcrumb
          styleClass="custom-breadcrumb"
          [model]="breadcrumbItems()"
          [home]="{ icon: 'pi pi-home', routerLink: '/', ariaLabel: 'Home' }"
        >
        </p-breadcrumb>
      </div>

      <!-- Page Header -->
      <section class="page-header mb-5" role="region" aria-labelledby="step3-heading">
        <div class="header-content">
          <div class="header-left">
            <i class="pi pi-folder-open header-icon" aria-hidden="true"></i>
            <div class="header-text">
              <h1 id="step3-heading">Gestione Documenti</h1>
              <p class="professional-info">
                @if (isSelectedEntityProfessional()) {
                  <strong>{{ selectedProfessionalBusinessName() }}</strong><br>
                  ID: {{ selectedProfessionalId() }}

                @if (selectedProfessionalCategory()) {
                  &nbsp;·&nbsp;{{ selectedProfessionalCategory() }}
                }
              } @else if (selectedSupplierId()) {
                @if (selectedSupplierDisplayName()) {
                  <strong>{{ selectedSupplierDisplayName() }}</strong><br>
                }
                ID: {{ selectedSupplierId() }}
              }
              </p>
              <p class="subtitle">Seleziona i template e carica documenti a corredo</p>
            </div>
          </div>
          <!-- Completion Indicator -->
          <div class="completion-badge" [attr.aria-label]="(isSelectedEntityProfessional() ? isStep4Completed() : isStep3Completed()) ? 'Step completato' : 'Step non completato'">
            @if (isSelectedEntityProfessional() ? isStep4Completed() : isStep3Completed()) {
              <i class="pi pi-check-circle completed-icon" aria-hidden="true"></i>
              <span class="completion-text">Completato</span>
            }
            @else {
              <i class="pi pi-circle incomplete-icon" aria-hidden="true"></i>
              <span class="completion-text">Da completare</span>
            }
          </div>
        </div>
      </section>

      <form [formGroup]="step3Form" (ngSubmit)="saveStep3AndGenerate()" class="p-fluid">
        <!-- Sezione: Selezione Template -->
        <section
          class="surface-0 border-round p-5 mb-4 shadow-1"
          role="region"
          aria-labelledby="template-documenti-heading"
        >
          <div class="flex align-items-center gap-3 mb-4 pb-3 border-bottom-1 border-200">
            <i class="pi pi-file text-2xl" style="color: #00125b" aria-hidden="true"></i>
            <h2
              id="template-documenti-heading"
              class="text-xl font-semibold m-0"
              style="color: #00125b"
            >
              Template Documenti
            </h2>
          </div>

          <div class="grid">
            <!-- Template Contratto -->
            <div class="field col-12 md:col-6">
              <label class="font-semibold block mb-3">
                Template Contratto <span class="text-red-500">*</span>
              </label>

              @if (!selectedTemplateContratto()) {
                <!-- Pulsante per aprire il dialog se non c'è selezione -->
                <p-button
                  label="Seleziona dal Registro Template"
                  icon="pi pi-search"
                  [outlined]="true"
                  styleClass="w-full justify-content-center"
                  (onClick)="openTemplateContrattoDialog()"
                  [attr.aria-label]="'Seleziona template contratto dal registro'"
                  title="Seleziona template contratto dal registro"
                ></p-button>
                <small class="text-muted block mt-2">
                  <i class="pi pi-info-circle mr-1" aria-hidden="true"></i>
                  Seleziona un template contratto dal registro
                </small>
              } @else {
                <!-- Card del template selezionato -->
                <div class="surface-100 border-round p-4 border-1 border-300">
                  <div class="flex justify-content-between align-items-start">
                    <div class="flex-1">
                      <div class="flex align-items-center gap-2 mb-2">
                        <i class="pi pi-file text-xl" style="color: #00125b" aria-hidden="true"></i>
                        <h4 class="text-lg font-semibold m-0">
                          {{ selectedTemplateContratto().label }}
                        </h4>
                      </div>
                    </div>
                    <div class="flex gap-2">
                      <p-button
                        icon="pi pi-pencil"
                        [text]="true"
                        [rounded]="true"
                        severity="secondary"
                        pTooltip="Cambia template"
                        tooltipPosition="top"
                        (onClick)="openTemplateContrattoDialog()"
                        [attr.aria-label]="'Cambia template contratto selezionato'"
                        title="Cambia template contratto selezionato"
                      ></p-button>
                      <p-button
                        icon="pi pi-times"
                        [text]="true"
                        [rounded]="true"
                        severity="danger"
                        pTooltip="Rimuovi selezione"
                        tooltipPosition="top"
                        (onClick)="clearTemplateContratto()"
                        [attr.aria-label]="'Rimuovi template contratto selezionato'"
                        title="Rimuovi template contratto selezionato"
                      ></p-button>
                    </div>
                  </div>
                </div>
              }

              @if (
                step3Form.get('templateContratto')?.invalid &&
                step3Form.get('templateContratto')?.touched
              ) {
                <small class="p-error block mt-2">
                  <i class="pi pi-exclamation-triangle mr-1"></i>
                  Il template contratto è obbligatorio
                </small>
              }
            </div>

            <!-- Template Atto di Nomina - Visibile solo se GDPR è abilitato -->
            @if (shouldShowAttoDiNomina()) {
              <div class="field col-12 md:col-6">
                <label class="font-semibold block mb-3">
                  Template Atto di Nomina <span class="text-red-500">*</span>
                </label>

                @if (!selectedTemplateAttoNomina()) {
                  <!-- Pulsante per aprire il dialog se non c'è selezione -->
                  <p-button
                    label="Seleziona dal Registro Template"
                    icon="pi pi-search"
                    [outlined]="true"
                    styleClass="w-full justify-content-center"
                    (onClick)="openTemplateAttoNominaDialog()"
                    [attr.aria-label]="'Seleziona template atto di nomina dal registro'"
                    title="Seleziona template atto di nomina dal registro"
                  ></p-button>
                  <small class="text-muted block mt-2">
                    <i class="pi pi-info-circle mr-1" aria-hidden="true"></i>
                    Seleziona un template atto di nomina dal registro
                  </small>
                } @else {
                  <!-- Card del template selezionato -->
                  <div class="surface-100 border-round p-4 border-1 border-300">
                    <div class="flex justify-content-between align-items-start">
                      <div class="flex-1">
                        <div class="flex align-items-center gap-2 mb-2">
                          <i
                            class="pi pi-file text-xl"
                            style="color: #00125b"
                            aria-hidden="true"
                          ></i>
                          <h4 class="text-lg font-semibold m-0">
                            {{ selectedTemplateAttoNomina().label }}
                          </h4>
                        </div>
                      </div>
                      <div class="flex gap-2">
                        <p-button
                          icon="pi pi-pencil"
                          [text]="true"
                          [rounded]="true"
                          severity="secondary"
                          pTooltip="Cambia template"
                          tooltipPosition="top"
                          (onClick)="openTemplateAttoNominaDialog()"
                          [attr.aria-label]="'Cambia template atto di nomina selezionato'"
                          title="Cambia template atto di nomina selezionato"
                        ></p-button>
                        <p-button
                          icon="pi pi-times"
                          [text]="true"
                          [rounded]="true"
                          severity="danger"
                          pTooltip="Rimuovi selezione"
                          tooltipPosition="top"
                          (onClick)="clearTemplateAttoNomina()"
                          [attr.aria-label]="'Rimuovi template atto di nomina selezionato'"
                          title="Rimuovi template atto di nomina selezionato"
                        ></p-button>
                      </div>
                    </div>
                  </div>
                }

                @if (
                  step3Form.get('templateAttoNomina')?.invalid &&
                  step3Form.get('templateAttoNomina')?.touched
                ) {
                  <small class="p-error block mt-2">
                    <i class="pi pi-exclamation-triangle mr-1"></i>
                    Il template atto di nomina è obbligatorio
                  </small>
                }
              </div>
            }

            <div class="field col-12">
              <small class="text-muted block">
                <i class="pi pi-info-circle mr-1"></i>
                I documenti verranno generati utilizzando i template selezionati con i dati del
                fornitore.
              </small>
            </div>
          </div>
        </section>

        <!-- Sezione: Caricamento Documenti a Corredo -->
        <section class="surface-0 border-round p-5 mb-4 shadow-1">
          <div class="flex align-items-center gap-3 mb-4 pb-3 border-bottom-1 border-200">
            <i class="pi pi-folder-open text-2xl" style="color: #00125b"></i>
            <h2 class="text-xl font-semibold m-0" style="color: #00125b">
              Documenti a Corredo <span class="text-600 font-normal">(Opzionale)</span>
            </h2>
            <p-tag
              [value]="
                uploadedDocuments().length +
                ' ' +
                (uploadedDocuments().length === 1 ? 'documento' : 'documenti')
              "
              severity="secondary"
            ></p-tag>
          </div>

          <!-- Tabella documenti -->
          <p-table
            [value]="uploadedDocuments()"
            [tableStyle]="{ 'min-width': '50rem' }"
            styleClass="p-datatable-sm"
            [paginator]="false"
            responsiveLayout="scroll"
            [ariaLabel]="'Tabella documenti contrattuali'"
          >
            <ng-template #header>
              <tr>
                <th style="width: 45%">Nome File</th>
                <th style="width: 15%">Tipo</th>
                <th style="width: 20%">Data Caricamento</th>
                <th style="width: 20%" class="text-right">Azioni</th>
              </tr>
            </ng-template>
            <ng-template #body let-doc>
              <tr>
                <td>
                  <div class="flex align-items-start gap-2">
                    <i
                      [class]="getFileIcon(doc.file.name || doc.title)"
                      [style.color]="
                        getFileTypeSeverity(doc.file.name || doc.title) === 'danger'
                          ? '#ef4444'
                          : getFileTypeSeverity(doc.file.name || doc.title) === 'success'
                            ? '#22c55e'
                            : getFileTypeSeverity(doc.file.name || doc.title) === 'warn'
                              ? '#f97316'
                              : '#3b82f6'
                      "
                      class="text-xl"
                      aria-hidden="true"
                    ></i>
                    <div class="flex flex-column">
                      <span class="font-medium">{{ doc.file.name || doc.title }}</span>
                      @if (doc.title && doc.title !== doc.file.name) {
                        <small class="text-600">{{ doc.title }}</small>
                      }
                    </div>
                  </div>
                </td>
                <td>
                  <p-tag
                    [value]="getFileExtension(doc.file.name || doc.title)"
                    [severity]="getFileTypeSeverity(doc.file.name || doc.title)"
                  ></p-tag>
                </td>
                <td>{{ formatUploadDate(doc.uploadedAt) }}</td>
                <td class="text-right">
                  <div class="flex gap-2 justify-content-end">
                    <p-button
                      icon="pi pi-download"
                      [rounded]="true"
                      [text]="true"
                      severity="secondary"
                      size="small"
                      (onClick)="onDownloadDocument(doc)"
                      [ariaLabel]="'Scarica ' + (doc.file.name || doc.title)"
                      pTooltip="Scarica"
                      tooltipPosition="top"
                      title="Scarica"
                    ></p-button>
                    <p-button
                      icon="pi pi-trash"
                      [rounded]="true"
                      [text]="true"
                      severity="danger"
                      size="small"
                      (onClick)="removeDocument(doc)"
                      [ariaLabel]="'Elimina ' + (doc.file.name || doc.title)"
                      pTooltip="Elimina"
                      tooltipPosition="top"
                      title="Elimina"
                    ></p-button>
                  </div>
                </td>
              </tr>
            </ng-template>
            <ng-template #emptymessage>
              <tr>
                <td colspan="4" class="text-center py-4 text-color-secondary">
                  <i
                    class="pi pi-inbox text-4xl mb-3"
                    style="display: block"
                    aria-hidden="true"
                  ></i>
                  Nessun documento caricato
                </td>
              </tr>
            </ng-template>
          </p-table>

          <!-- Button section footer -->
          <div class="flex justify-content-end gap-2 mt-4">
            <p-button
              label="Carica Nuovo Documento"
              icon="pi pi-upload"
              (onClick)="openUploadDialog()"
              [outlined]="true"
              size="small"
              [attr.aria-label]="'Carica nuovo documento a corredo'"
              pTooltip="Carica un nuovo documento allegato"
              tooltipPosition="top"
              title="Carica un nuovo documento allegato"
            ></p-button>
          </div>
        </section>

        <!-- Sezione: Note (Opzionale) -->
        <section class="surface-0 border-round p-5 mb-4 shadow-1">
          <div class="flex align-items-center gap-3 mb-4 pb-3 border-bottom-1 border-200">
            <i class="pi pi-comment text-2xl" style="color: #00125b"></i>
            <h2 class="text-xl font-semibold m-0" style="color: #00125b">Note</h2>
          </div>

          <div class="grid">
            <div class="field col-12">
              <label for="documentNotes" class="font-semibold block mb-2">Note aggiuntive</label>
              <textarea
                pTextarea
                id="documentNotes"
                formControlName="documentNotes"
                rows="3"
                class="w-full"
                placeholder="Note aggiuntive sulla generazione dei documenti"
              ></textarea>
            </div>
          </div>
        </section>

        <!-- Action Buttons -->
        <div class="flex flex-wrap justify-content-between gap-3 mt-4">
          <p-button
            label="Indietro"
            icon="pi pi-arrow-left"
            severity="secondary"
            [outlined]="true"
            (onClick)="goToPreviousStep()"
            [disabled]="isGeneratingWord()"
            title="Torna al passo precedente"
          ></p-button>
          <div class="flex flex-wrap gap-3">
            <p-button
              label="Salva"
              icon="pi pi-save"
              [outlined]="true"
              styleClass="btn-draft"
              (onClick)="saveDraft(3)"
              [disabled]="isGeneratingWord()"
              aria-label="Salva come bozza senza validazione"
              title="Salva come bozza senza validazione"
            ></p-button>
            <p-button
              [label]="isGeneratingWord() ? 'Generazione in corso...' : 'Salva e Genera Contratto'"
              [icon]="isGeneratingWord() ? 'pi pi-spin pi-spinner' : 'pi pi-file-word'"
              iconPos="left"
              type="submit"
              [disabled]="step3Form.invalid || isGeneratingWord() || !canGenerateDocument()"
              [loading]="isGeneratingWord()"
              [pTooltip]="generateDocumentDisabledReason()"
              tooltipPosition="top"
              [attr.aria-label]="generateDocumentDisabledReason()"
              [title]="generateDocumentDisabledReason()"
            ></p-button>
          </div>
        </div>
      </form>
    }
  </div>
</div>

<!-- Dialog per selezione Responsabile -->
<p-dialog
  [(visible)]="showResponsibleDialog"
  [modal]="true"
  [closable]="true"
  [draggable]="false"
  [style]="{ width: '90vw', maxWidth: '1200px' }"
  [contentStyle]="{ padding: '0' }"
  (onHide)="closeResponsibleDialog()"
  ariaLabel="Dialog selezione responsabile"
  aria-labelledby="responsabile-dialog-title"
>
  <ng-template pTemplate="header">
    <div class="flex align-items-center justify-content-between w-full">
      <div class="flex align-items-center gap-2">
        <i class="pi pi-building text-xl" style="color: #00125b" aria-hidden="true"></i>
        <span id="responsabile-dialog-title" class="text-xl font-semibold" style="color: #00125b"
          >Seleziona Responsabile dal Registro</span
        >
      </div>
      <p-button
        label="Crea Nuovo"
        icon="pi pi-plus"
        severity="success"
        [outlined]="true"
        (onClick)="openCreateResponsibleDialog()"
        ariaLabel="Crea nuovo responsabile"
        title="Crea nuovo responsabile"
      ></p-button>
    </div>
  </ng-template>

  <div class="p-4" role="region" aria-labelledby="responsabile-dialog-title">
    <app-registry-table
      [title]="''"
      [columns]="responsibleColumns"
      [data]="responsibleTableData()"
      [loading]="loadingResponsibles()"
      [showClearFiltersButton]="false"
      emptyStateMessage="Nessun responsabile trovato nel registro."
      (onSelectClick)="selectResponsible($event)"
    />
  </div>
</p-dialog>

<!-- Dialog per selezione Sub-Responsabile -->
<p-dialog
  [(visible)]="showSubResponsibleDialog"
  [modal]="true"
  [closable]="true"
  [draggable]="false"
  [style]="{ width: '90vw', maxWidth: '1200px' }"
  [contentStyle]="{ padding: '0' }"
  (onHide)="closeSubResponsibleDialog()"
  ariaLabel="Dialog selezione sub-responsabile"
  aria-labelledby="subresponsabile-dialog-title"
>
  <ng-template pTemplate="header">
    <div class="flex align-items-center justify-content-between w-full">
      <div class="flex align-items-center gap-2">
        <i class="pi pi-sitemap text-xl" style="color: #00125b" aria-hidden="true"></i>
        <span id="subresponsabile-dialog-title" class="text-xl font-semibold" style="color: #00125b"
          >Seleziona Sub-Responsabile dal Registro</span
        >
      </div>
      <p-button
        label="Crea Nuovo"
        icon="pi pi-plus"
        severity="success"
        [outlined]="true"
        (onClick)="openCreateSubResponsibleDialog()"
        ariaLabel="Crea nuovo sub-responsabile"
        title="Crea nuovo sub-responsabile"
      ></p-button>
    </div>
  </ng-template>

  <div class="p-4" role="region" aria-labelledby="subresponsabile-dialog-title">
    <app-registry-table
      [title]="''"
      [columns]="subResponsibleColumns"
      [data]="subResponsibleTableData()"
      [loading]="loadingSubResponsibles()"
      [showClearFiltersButton]="false"
      emptyStateMessage="Nessun sub-responsabile trovato nel registro. Clicca 'Crea Nuovo' per aggiungerne uno."
      (onSelectClick)="onSubResponsibleSelect($event)"
    />
  </div>
</p-dialog>
<!-- Dialog per selezione Template Contratto -->
<p-dialog
  [(visible)]="showTemplateContrattoDialog"
  [modal]="true"
  [closable]="true"
  [draggable]="false"
  [style]="{ width: '90vw', maxWidth: '1200px' }"
  [contentStyle]="{ padding: '0' }"
  (onHide)="closeTemplateContrattoDialog()"
  ariaLabel="Dialog selezione template contratto"
  aria-labelledby="template-contratto-dialog-title"
>
  <ng-template pTemplate="header">
    <div class="flex align-items-center justify-content-between w-full">
      <div class="flex align-items-center gap-2">
        <i class="pi pi-file text-xl" style="color: #00125b" aria-hidden="true"></i>
        <span
          id="template-contratto-dialog-title"
          class="text-xl font-semibold"
          style="color: #00125b"
          >Seleziona Template Contratto</span
        >
      </div>
    </div>
  </ng-template>

  <div class="p-4" role="region" aria-labelledby="template-contratto-dialog-title">
    <app-registry-table
      [title]="''"
      [columns]="templateContrattoColumns"
      [data]="templateContrattoTableData()"
      [loading]="false"
      [showClearFiltersButton]="false"
      emptyStateMessage="Nessun template contratto disponibile."
      (onSelectClick)="selectTemplateContratto($event)"
    />
  </div>
</p-dialog>

<!-- Dialog per selezione Template Atto di Nomina -->
<p-dialog
  [(visible)]="showTemplateAttoNominaDialog"
  [modal]="true"
  [closable]="true"
  [draggable]="false"
  [style]="{ width: '90vw', maxWidth: '1200px' }"
  [contentStyle]="{ padding: '0' }"
  (onHide)="closeTemplateAttoNominaDialog()"
  ariaLabel="Dialog selezione template atto di nomina"
  aria-labelledby="template-atto-dialog-title"
>
  <ng-template pTemplate="header">
    <div class="flex align-items-center justify-content-between w-full">
      <div class="flex align-items-center gap-2">
        <i class="pi pi-file text-xl" style="color: #00125b" aria-hidden="true"></i>
        <span id="template-atto-dialog-title" class="text-xl font-semibold" style="color: #00125b"
          >Seleziona Template Atto di Nomina</span
        >
      </div>
    </div>
  </ng-template>

  <div class="p-4" role="region" aria-labelledby="template-atto-dialog-title">
    <app-registry-table
      [title]="''"
      [columns]="templateAttoNominaColumns"
      [data]="templateAttoNominaTableData()"
      [loading]="false"
      [showClearFiltersButton]="false"
      emptyStateMessage="Nessun template atto di nomina disponibile."
      (onSelectClick)="selectTemplateAttoNomina($event)"
    />
  </div>
</p-dialog>

<!-- Checklist Dialog -->
<p-dialog
  [(visible)]="showChecklistDialogValue"
  [modal]="true"
  [dismissableMask]="true"
  [style]="{ width: '50vw', maxWidth: '800px' }"
  [draggable]="false"
  [resizable]="false"
  [closable]="true"
  [closeOnEscape]="true"
  (onHide)="onChecklistDialogHide()"
  styleClass="checklist-dialog"
  role="dialog"
  aria-labelledby="checklistDialogTitle"
  aria-describedby="checklistDialogDesc"
  [attr.aria-busy]="loadingChecklist()"
>
  <ng-template pTemplate="header">
    <div class="flex align-items-center justify-content-between w-full">
      <h2 id="checklistDialogTitle" class="m-0 text-xl font-semibold">Checklist di Controllo</h2>
      @if (checklistHasUnsavedChanges()) {
        <span
          class="text-sm text-orange-600 font-medium flex align-items-center gap-1"
          role="status"
          aria-live="polite"
        >
          <i class="pi pi-exclamation-circle" aria-hidden="true"></i>
          <span>Modifiche non salvate</span>
        </span>
      }
    </div>
  </ng-template>

  <div class="checklist-content" id="checklistDialogDesc">
    @if (loadingChecklist()) {
      <div
        class="flex justify-content-center align-items-center p-5"
        role="status"
        aria-live="polite"
      >
        <p-progressSpinner
          [style]="{ width: '50px', height: '50px' }"
          strokeWidth="4"
          ariaLabel="Caricamento checklist in corso"
        ></p-progressSpinner>
        <span class="sr-only">Caricamento checklist in corso...</span>
      </div>
    } @else if (checklistData()?.rows && checklistData()!.rows.length > 0) {
      <div class="checklist-items" role="list" aria-label="Lista attività checklist">
        @for (row of checklistData()!.rows; track row.rowId) {
          <div
            class="checklist-item p-3 mb-2 border-round surface-border border-1"
            [class.checklist-item-completed]="row.completed"
            role="listitem"
          >
            <div class="flex align-items-start gap-3">
              <p-checkbox
                [binary]="true"
                [(ngModel)]="row.completed"
                (onChange)="toggleChecklistRow(row)"
                [inputId]="'checklist-' + row.rowId"
                [ariaLabel]="
                  row.completed
                    ? 'Attività completata: ' + row.rowValue
                    : 'Attività da completare: ' + row.rowValue
                "
              ></p-checkbox>
              <label
                [for]="'checklist-' + row.rowId"
                class="cursor-pointer flex-1"
                [class.line-through]="row.completed"
                [class.text-600]="row.completed"
              >
                {{ row.rowValue }}
              </label>
            </div>
          </div>
        }
      </div>
    } @else {
      <div class="flex justify-content-center align-items-center p-5" role="status">
        <p class="text-500">Nessuna attività da mostrare</p>
      </div>
    }
  </div>

  <ng-template pTemplate="footer">
    <div class="flex justify-content-end gap-2" role="group" aria-label="Azioni checklist">
      <p-button
        label="Annulla"
        icon="pi pi-times"
        severity="secondary"
        [outlined]="true"
        (onClick)="closeChecklistDialog()"
        [disabled]="loadingChecklist()"
        ariaLabel="Annulla modifiche e chiudi checklist"
        title="Annulla modifiche e chiudi checklist"
      ></p-button>
      <p-button
        label="Salva"
        icon="pi pi-check"
        (onClick)="saveChecklist()"
        [disabled]="loadingChecklist() || !checklistHasUnsavedChanges()"
        [loading]="loadingChecklist()"
        ariaLabel="Salva modifiche checklist"
        title="Salva modifiche checklist"
      ></p-button>
    </div>
  </ng-template>
</p-dialog>
<!-- ================= DIALOG: CARICA NUOVO DOCUMENTO ================= -->
<p-dialog
  [(visible)]="showUploadDialog"
  [modal]="true"
  [closable]="true"
  [closeOnEscape]="true"
  [dismissableMask]="true"
  [draggable]="false"
  [resizable]="false"
  styleClass="w-11 md:w-8 lg:w-6"
  ariaLabel="Dialog caricamento nuovo documento"
  aria-labelledby="upload-dialog-title"
>
  <ng-template #header>
    <div class="flex align-items-center gap-2">
      <i class="pi pi-upload text-xl" style="color: #00125b" aria-hidden="true"></i>
      <span id="upload-dialog-title" class="text-xl font-semibold">Carica Nuovo Documento</span>
    </div>
  </ng-template>

  <div class="flex flex-column gap-4">
    <!-- Titolo Documento -->
    <div>
      <label for="dialog-attachment-title" class="block mb-2 font-semibold">
        Titolo Documento <span class="text-red-600">*</span>
      </label>
      <input
        id="dialog-attachment-title"
        type="text"
        pInputText
        [ngModel]="currentDocumentTitle()"
        (ngModelChange)="currentDocumentTitle.set($event)"
        placeholder="Es. Allegato tecnico, Condizioni particolari, ecc."
        class="w-full"
        [disabled]="isUploadingAttachment()"
        aria-required="true"
        [attr.aria-invalid]="!currentDocumentTitle().trim() ? 'true' : 'false'"
        [attr.aria-describedby]="
          !currentDocumentTitle().trim()
            ? 'dialog-attachment-title-help dialog-attachment-title-error'
            : 'dialog-attachment-title-help'
        "
        autofocus
      />
      <small id="dialog-attachment-title-help" class="text-600 block mt-1">
        <i class="pi pi-info-circle mr-1" aria-hidden="true"></i>
        Inserisci un titolo descrittivo per il documento
      </small>
      @if (!currentDocumentTitle().trim()) {
        <small id="dialog-attachment-title-error" class="text-red-500 block mt-1" role="alert">
          Inserisci un titolo descrittivo per il documento.
        </small>
      }
    </div>

    <!-- File Upload -->
    <div>
      <label id="dialog-attachment-file-label" class="block mb-2 font-semibold">
        Seleziona File
      </label>
      <p-fileupload
        #attachmentFileUpload
        name="dialog-attachment"
        [auto]="false"
        [customUpload]="true"
        (uploadHandler)="onDocumentUpload($event)"
        accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx"
        [maxFileSize]="10485760"
        [disabled]="isUploadingAttachment()"
        [showUploadButton]="false"
        [showCancelButton]="false"
        styleClass="custom-fileupload"
        [attr.aria-label]="'Carica documento a corredo del contratto'"
        [attr.aria-labelledby]="'dialog-attachment-file-label'"
        class="w-full"
      >
        <ng-template pTemplate="header">
          <!-- Header vuoto per nascondere i pulsanti di default -->
        </ng-template>
        <ng-template
          pTemplate="content"
          let-files
          let-chooseCallback="chooseCallback"
          let-removeFileCallback="removeFileCallback"
        >
          @if (isUploadingAttachment()) {
            <div class="flex align-items-center justify-content-center gap-2 p-5">
              <i class="pi pi-spin pi-spinner text-2xl" aria-hidden="true"></i>
              <span class="font-semibold">Caricamento in corso...</span>
            </div>
          } @else if (files && files.length === 0) {
            <div class="flex flex-column align-items-center justify-content-center p-5 text-center">
              <i class="pi pi-cloud-upload text-6xl mb-3 text-400" aria-hidden="true"></i>
              <p class="m-0 mb-2 font-semibold text-900">Trascina qui il documento</p>
              <p class="m-0 mb-3 text-sm text-600">oppure</p>
              <p-button
                label="Scegli File"
                icon="pi pi-folder-open"
                (onClick)="chooseCallback()"
                [outlined]="true"
                ariaLabel="Seleziona file allegato da caricare"
              ></p-button>
            </div>
          } @else {
            <div class="p-3">
              @for (file of files; track file.name) {
                <div
                  class="flex align-items-center justify-content-between p-3 border-1 border-200 border-round mb-2"
                >
                  <div class="flex align-items-center gap-3">
                    <i class="pi pi-file text-3xl text-primary" aria-hidden="true"></i>
                    <div>
                      <div class="font-semibold text-900">{{ file.name }}</div>
                      <small class="text-600">{{ formatFileSize(file.size) }}</small>
                    </div>
                  </div>
                  <p-button
                    icon="pi pi-times"
                    [rounded]="true"
                    [text]="true"
                    severity="danger"
                    (onClick)="removeFileCallback(file)"
                    [ariaLabel]="'Rimuovi ' + file.name"
                  ></p-button>
                </div>
              }
              <p-button
                label="Scegli un Altro File"
                icon="pi pi-folder-open"
                (onClick)="chooseCallback()"
                [outlined]="true"
                size="small"
                class="w-full mt-2"
                ariaLabel="Seleziona un file diverso da caricare"
              ></p-button>
            </div>
          }
        </ng-template>
      </p-fileupload>
      <small class="text-600 block mt-2">
        <strong>Formati supportati:</strong> PDF, Word (DOC/DOCX), Excel (XLS/XLSX), PowerPoint
        (PPT/PPTX)
      </small>
      <small class="text-600 block mt-1"> <strong>Dimensione massima:</strong> 10 MB </small>
    </div>

    <!-- Live region per screen reader -->
    <div aria-live="polite" aria-atomic="true" class="sr-only">
      @if (isUploadingAttachment()) {
        Caricamento del documento in corso, attendere...
      }
    </div>
  </div>

  <ng-template #footer>
    <p-button
      label="Annulla"
      severity="secondary"
      [outlined]="true"
      icon="pi pi-times"
      (onClick)="closeUploadDialog()"
      [disabled]="isUploadingAttachment()"
      ariaLabel="Annulla e chiudi il dialog di caricamento"
      title="Chiudi senza caricare"
      pTooltip="Chiudi senza caricare"
      tooltipPosition="top"
    ></p-button>
    <p-button
      label="Carica"
      icon="pi pi-upload"
      (onClick)="attachmentFileUpload.upload()"
      [disabled]="
        isUploadingAttachment() ||
        !currentDocumentTitle().trim() ||
        !attachmentFileUpload.files ||
        attachmentFileUpload.files.length === 0
      "
      [loading]="isUploadingAttachment()"
      ariaLabel="Carica documento nel sistema"
      [title]="
        !currentDocumentTitle().trim()
          ? 'Inserisci un titolo'
          : !attachmentFileUpload.files || attachmentFileUpload.files.length === 0
            ? 'Seleziona un file'
            : 'Carica documento'
      "
      [pTooltip]="
        !currentDocumentTitle().trim()
          ? 'Inserisci un titolo'
          : !attachmentFileUpload.files || attachmentFileUpload.files.length === 0
            ? 'Seleziona un file'
            : 'Carica documento'
      "
      [attr.aria-describedby]="
        !currentDocumentTitle().trim() ? 'dialog-attachment-title-error' : null
      "
      tooltipPosition="top"
    ></p-button>
  </ng-template>
</p-dialog>
